import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { sameIdentity } from '../../services/identity';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { GraphQLPMSService } from '../../services/graphqlPms';
import { loadDirectReportsWithStatus, loadAllActiveEmployees, getUserByEmail } from '../../services/userLoader';
import {
  getAttachmentDownloadUrl,
  uploadReviewAttachmentFile,
  validateAttachmentFile,
  AttachmentUrlError,
} from '../../services/pmsAttachments';
import { UserRole } from '../../types/user';
import { mapUserRoleToPMSRole, isPMSRoleHigherOrEqual } from '../../utils/pmsPermissions';
import { filterActiveCycles, filterCurrentCycleReviewCycles } from '../../utils/pmsFilters';
import {
  PMSRole,
  ReviewCycle,
  ReviewCycleStatus,
  ReviewPhase,
  Review,
  ReviewType,
  Objective,
  ObjectiveEvaluationSummary,
  ObjectiveStatus,
  RatingScale,
  Attachment,
  AttachmentParentType,
} from '../../types/pms';
import ReviewCycleSelector from './ReviewCycleSelector';
import RightSidePanel from './RightSidePanel';
import RatingDisplay from './RatingDisplay';
import ProgressBar from './ProgressBar';
import ProgressSlider from './ProgressSlider';
import { getRatingLabel } from '../../services/pmsRatingCalculator';
import {
  appendReviewDiscussion,
  appendReviewHistory,
  buildReviewDiscussionMessage,
  buildReviewHistoryEntry,
  canAcknowledgeReview,
  getReviewDiscussion,
  canEditSelfReview,
  canManagerReview,
  dedupeReviewsByEmployeeCycle,
  resolveCanonicalReview,
  resolveReviewTypeForCycle,
  computeManagerReviewProgress,
  deriveReviewStatus,
  canReturnToEmployee,
  employeeCommentOf,
  employeeProgressOf,
  managerCommentDraftOf,
  canAcceptNewReview,
  managerCommentOf,
  resolveReviewerIdentity,
  isResolvedReviewerValue,
  getReturnReason,
  enrichObjectiveRatings,
  formatRelativeTime,
  getManagerReviewBlockReason,
  getReviewWorkflowStatusLabel,
  getSelfReviewProgress,
  computeTeamReviewProgress,
  inferReviewWorkflowStatus,
  isReviewEditable,
  isReviewInManagerScope,
  ReviewStatus,
  SelfReviewProgress,
} from '../../services/pmsWorkflow';
import SelfReviewForm, { SelfReviewSubmission } from './SelfReviewForm';
import ReviewDiscussion from './ReviewDiscussion';
import ReviewProgressSummary from './ReviewProgressSummary';
import ReviewTimeline from './ReviewTimeline';
import { ReportExportService } from '../../services/reportExport';
import {
  notifyFinalReviewAvailable,
  notifyHighLevelReviewPublished,
  notifyMidYearReviewSubmitted,
  notifyReviewAcknowledged,
  notifyReviewAssigned,
} from '../../services/inAppNotification';
import {
  ReviewWorkflowStatus,
  ReviewHistoryAction,
  ReviewHistoryEntry,
  ReviewCycleType,
  REVIEW_CYCLE_TYPE_LABELS,
} from '../../types/pms';
import './TeamReviews.css';
import './GoalsTable.css';
import './pms-premium.css';
/*
 * My Reviews only — every rule inside is scoped to .pms-employee-reviews-route, the class the
 * page root carries when mode !== 'team'. Imported last so it outranks TeamReviews.css on ties.
 * Team Reviews shares this component and must render exactly as before, so nothing here may be
 * written unscoped.
 */
import './EmployeeReviewsPage.css';

/**
 * TeamReviews Component
 * Handles both Manager and Employee views for team performance reviews.
 * - Manager: can create reviews for team members, view all reviews
 * - Employee: can view assigned reviews, acknowledge them, add attachments
 */
interface TeamReviewsProps {
  mode?: 'my' | 'team';
}

const TeamReviews: React.FC<TeamReviewsProps> = ({ mode = 'my' }) => {
  const renderStart = performance.now();
  console.log('[Perf][TeamReviews] render start');
  const { user } = useAuth();

  const pmsRole = useMemo(
    () => (user ? mapUserRoleToPMSRole(user.role) : PMSRole.EMPLOYEE),
    [user]
  );

  const isManager = useMemo(
    () => isPMSRoleHigherOrEqual(pmsRole, PMSRole.MANAGER),
    [pmsRole]
  );
  const isTeamMode = mode === 'team';
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  // Shared state
  const [allCycles, setAllCycles] = useState<ReviewCycle[]>([]);
  /**
   * Annual objective cycle id → name, for grouping the review-cycle dropdown by parent.
   *
   * Built from the unfiltered fetch because the parents themselves are rows in the same table
   * that filterActiveCycles removes: annual cycles carry no annualCycleId and are typically
   * Completed, so they never survive into `allCycles`.
   */
  const [annualCycleNames, setAnnualCycleNames] = useState<Record<string, string>>({});
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [autoSelectingReviewCycle, setAutoSelectingReviewCycle] = useState(false);
  const hasAutoSelectedReviewCycle = React.useRef(false);
  const cyclesLoadedRef = React.useRef(false);

  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Load review cycles
  useEffect(() => {
    // Prevent duplicate load from React StrictMode double-mount
    if (cyclesLoadedRef.current && refreshKey === 0) return;
    cyclesLoadedRef.current = true;

    (async () => {
      const startedAt = performance.now();
      console.log('[Perf][TeamReviews] API start GraphQLPMSService.getAllReviewCycles');
      try {
        const cycles = await GraphQLPMSService.getAllReviewCycles();
        console.log('[Perf][TeamReviews] API complete GraphQLPMSService.getAllReviewCycles', {
          durationMs: Math.round(performance.now() - startedAt),
          cycleCount: cycles.length,
        });
        /*
         * Only ACTIVE child review cycles — employees should not see Closed/Archived cycles.
         *
         * On My Reviews the list is narrowed to the children of the objective cycle that is
         * CURRENTLY PUBLISHED — see filterCurrentCycleReviewCycles. It was narrowed to the
         * current calendar year before, which is not the same thing and was not enough: an
         * organisation that set up several objective cycles this year had them all survive,
         * and the dropdown listed seven at once for an employee who is in exactly one.
         *
         * Team Reviews keeps the full active list: a manager still has reason to open an older
         * cycle, and narrowing it was not asked for.
         */
        const display = (isTeamMode ? filterActiveCycles(cycles) : filterCurrentCycleReviewCycles(cycles))
          .sort((a, b) => b.startDate.localeCompare(a.startDate));
        console.log('[TeamReviews] Review cycle dropdown population:', {
          totalCycles: cycles.length,
          statusFilter: ReviewCycleStatus.ACTIVE,
          reviewTypeFilter: 'None',
          slicingApplied: false,
          deduplicationApplied: false,
          displayCycles: display.map((c) => ({ id: c.id, name: c.name, status: c.status, annualCycleId: c.annualCycleId })),
        });
        setAnnualCycleNames(
          cycles.reduce<Record<string, string>>((acc, cycle) => {
            acc[cycle.id] = cycle.name;
            return acc;
          }, {})
        );
        setAllCycles(display);
        setSelectedCycleId((currentId) => display.some((cycle) => cycle.id === currentId)
          ? currentId
          : display[0]?.id ?? '');
      } catch (err) {
        console.error('[TeamReviews] Failed to load cycles:', err);
      }
    })();
    // isTeamMode comes from a route-fixed prop, so it never actually changes for a mounted
    // instance — listed to keep the dependency set honest rather than to trigger reloads.
  }, [refreshKey, isTeamMode]);

  // Load reviews based on role
  useEffect(() => {
    if (!user || !selectedCycleId) {
      setLoading(false);
      return;
    }
    /*
     * The latest request wins, rather than the first one blocking the rest.
     *
     * This used to guard with `if (reviewsLoadingRef.current) return`, which does not
     * de-duplicate a concurrent load — it *drops* it. The effect re-runs whenever
     * selectedCycleId or refreshKey changes, and the auto-select effect below calls
     * setSelectedCycleId while this fetch is still in flight. When that happened the new
     * cycle's reviews were never requested at all: the in-flight call finished, cleared the
     * flag and set loading false, and the component was left showing zero records for a cycle
     * that has them — "No review records for end q1", cleared by a later reload and back again
     * on the next refresh, which is exactly how it presented.
     *
     * A cancellation token fixes both halves. React StrictMode's double-invoke cancels its
     * first pass on cleanup so only the second commits, and a slow response for a
     * previously-selected cycle can no longer overwrite a newer one — the old effect had no
     * cancellation at all, so that race was live too.
     */
    let cancelled = false;

    (async () => {
      const startedAt = performance.now();
      const queryName = isTeamMode
        ? 'GraphQLPMSService.getReviewsByReviewCycle'
        : 'GraphQLPMSService.getEmployeeReviews';
      console.log('[Perf][TeamReviews] API start', {
        queryName,
        selectedCycleId,
        userEmail: user.email,
        isManager,
        mode,
      });
      setLoading(true);
      try {
        if (isTeamMode) {
          const revs = await GraphQLPMSService.getReviewsByReviewCycle(selectedCycleId);
          console.log('[Perf][TeamReviews] API complete', {
            queryName,
            durationMs: Math.round(performance.now() - startedAt),
            reviewCount: revs.length,
            filteredReviewCount: revs.length,
            scope: isSuperAdmin ? 'all reviews' : 'selected cycle reviews before reporting-employee scope',
          });
          if (cancelled) return;
          setReviews(revs);
        } else {
          const revs = await GraphQLPMSService.getEmployeeReviews(user.email, selectedCycleId);
          console.log('[Perf][TeamReviews] API complete', {
            queryName,
            durationMs: Math.round(performance.now() - startedAt),
            reviewCount: revs.length,
            filteredReviewCount: revs.filter((r) => r.reviewCycleId === selectedCycleId).length,
          });
          if (cancelled) return;
          // Filter to selected cycle
          setReviews(revs);
        }
      } catch (err) {
        if (!cancelled) console.error('[TeamReviews] Failed to load reviews:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedCycleId, refreshKey]);

  useEffect(() => {
    if (
      hasAutoSelectedReviewCycle.current ||
      !user ||
      !isTeamMode ||
      !isManager ||
      allCycles.length === 0 ||
      !selectedCycleId
    ) {
      return;
    }

    let cancelled = false;
    const countVisibleReviews = async (cycleId: string) => {
      const cycleReviews = await GraphQLPMSService.getReviewsByReviewCycle(cycleId);
      return cycleReviews.length;
    };

    (async () => {
      setAutoSelectingReviewCycle(true);
      try {
        const currentCount = await countVisibleReviews(selectedCycleId);
        if (cancelled || currentCount > 0) {
          hasAutoSelectedReviewCycle.current = true;
          return;
        }

        // Limit to checking max 3 other cycles to avoid excessive API calls
        const otherCycles = allCycles.filter(c => c.id !== selectedCycleId).slice(0, 3);
        for (const cycle of otherCycles) {
          if (cancelled) return;
          const cycleCount = await countVisibleReviews(cycle.id);
          if (cancelled) return;
          if (cycleCount > 0) {
            setSelectedCycleId(cycle.id);
            break;
          }
        }
      } catch (err) {
        console.error('[TeamReviews] Failed to auto-select review cycle with records:', err);
      } finally {
        hasAutoSelectedReviewCycle.current = true;
        if (!cancelled) {
          setAutoSelectingReviewCycle(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      setAutoSelectingReviewCycle(false);
    };
  }, [allCycles, isManager, isSuperAdmin, isTeamMode, selectedCycleId, user]);

  useEffect(() => {
    console.log('[Perf][TeamReviews] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      loading,
      cycles: allCycles.length,
      reviews: reviews.length,
      selectedCycleId,
      isManager,
      mode,
    });
  });

  if (!user) return null;
  const pageTitle = isTeamMode ? 'Team Reviews' : 'My Reviews';
  const pageSubtitle = isTeamMode
    ? 'Review team member performance and track review progress.'
    : 'View reviews about you, ratings, and manager feedback.';

  return (
    <div className={`pms-team-reviews${isTeamMode ? '' : ' pms-employee-reviews-route'}`}>
      <div className="pms-team-reviews__header">
        <div>
          <h1 className="pms-team-reviews__title">{pageTitle}</h1>
          <p className="pms-team-reviews__subtitle">{pageSubtitle}</p>
        </div>
        <div className="pms-team-reviews__header-right">
          <ReviewCycleSelector
            cycles={allCycles}
            selectedCycleId={selectedCycleId}
            onChange={setSelectedCycleId}
            label="Review Cycle"
            annualCycleNames={annualCycleNames}
          />
          {isTeamMode && isManager && (
            <button
              className="pms-team-reviews__create-btn"
              onClick={() => setPanelOpen(true)}
            >
              + Create Review
            </button>
          )}
        </div>
      </div>

      {isTeamMode && isManager ? (
        <ManagerView
          reviews={reviews}
          loading={loading}
          allCycles={allCycles}
          selectedCycleId={selectedCycleId}
          userId={user!.email}
          userName={user!.name}
          isSuperAdmin={isSuperAdmin}
          autoSelectingReviewCycle={autoSelectingReviewCycle}
          onReviewCreated={reload}
          panelOpen={panelOpen}
          setPanelOpen={setPanelOpen}
        />
      ) : (
        <EmployeeView
          reviews={reviews}
          loading={loading}
          userId={user!.email}
          userName={user!.name}
          allCycles={allCycles}
          selectedCycleId={selectedCycleId}
          onReviewSubmitted={reload}
        />
      )}
    </div>
  );
};

function formatReviewDate(value?: string): string {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

async function loadReviewSnapshot(review: Review, view: 'manager' | 'employee'): Promise<Review> {
  console.log('[TeamReviews][ReviewSnapshot] load start', {
    view,
    reviewId: review.id,
    reviewCycleId: review.reviewCycleId,
    employeeId: review.employeeId,
    table: 'PMS_Final_Reviews',
  });
  const snapshot = await GraphQLPMSService.getReview(review.id);
  const resolved = snapshot || review;
  console.log('[TeamReviews][ReviewSnapshot] load complete', {
    view,
    reviewId: resolved.id,
    reviewCycleId: resolved.reviewCycleId,
    employeeId: resolved.employeeId,
    table: 'PMS_Final_Reviews',
    recordCount: snapshot ? 1 : 0,
    objectiveRecordCount: resolved.objectiveRatings.length,
  });
  return resolved;
}

/**
 * The attachment list both review views render.
 *
 * Shared rather than duplicated because the two copies it replaces had already drifted apart in
 * one respect that mattered — neither offered a way to open a file, but only one of them reported
 * a load failure — and a download that behaves differently for the manager than for the employee
 * is precisely the bug class being fixed.
 *
 * The filename is a button, not a span. That is the other half of the reported defect: both lists
 * printed two plain spans, so there was nothing to click. AttachmentList elsewhere in PMS does
 * render a download control, but neither review view uses it.
 */
/**
 * What the employee wrote when they submitted their self-review.
 *
 * submitSelfReview has always persisted employeeRating / employeeRatingDescription /
 * employeeComment onto the Review, and mapReview has always read them back — but nothing in
 * TeamReviews rendered any of the three. So the employee's own words vanished from the UI the
 * moment the panel closed (looking, on refresh, exactly like they had never saved) and the
 * manager evaluating the review could not see them at all. Only ReviewForm — the separate
 * per-employee deep-link route — ever displayed employeeComment.
 *
 * Rendered from the employee's own fields, never from overallComments or managerFinalComment:
 * this is the employee speaking, and it must not be able to appear under a manager heading.
 */
const EmployeeSelfReviewSummary: React.FC<{ review: Review; audience: 'employee' | 'manager' }> = ({
  review,
  audience,
}) => {
  const hasAnything =
    review.employeeRating || review.employeeRatingDescription || review.employeeComment;
  if (!hasAnything) return null;

  return (
    <section className="pms-self-summary">
      <h4 className="pms-self-summary__title">
        {audience === 'manager' ? "Employee's self-review" : 'Your self-review'}
      </h4>

      {review.employeeRating && (
        <div className="pms-self-summary__row">
          <span className="pms-self-summary__label">Self rating</span>
          <strong className="pms-self-summary__value">
            {getRatingLabel(review.employeeRating as RatingScale)}
          </strong>
        </div>
      )}

      {review.employeeRatingDescription && (
        <div className="pms-self-summary__block">
          <span className="pms-self-summary__label">Rating rationale</span>
          <p className="pms-self-summary__text">{review.employeeRatingDescription}</p>
        </div>
      )}

      {review.employeeComment && (
        <div className="pms-self-summary__block">
          <span className="pms-self-summary__label">Comment</span>
          <p className="pms-self-summary__text">{review.employeeComment}</p>
        </div>
      )}

      {review.employeeSubmittedAt && (
        <p className="pms-self-summary__stamp">
          Submitted {formatReviewDate(review.employeeSubmittedAt)} ·{' '}
          {formatRelativeTime(review.employeeSubmittedAt)}
        </p>
      )}
    </section>
  );
};

const ReviewAttachmentList: React.FC<{ attachments: Attachment[] }> = ({ attachments }) => {
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const handleOpen = async (attachment: Attachment) => {
    setError('');
    setBusyId(attachment.id);
    try {
      const url = await getAttachmentDownloadUrl(attachment);
      /*
       * noopener/noreferrer on a window opened to a signed S3 URL: without noopener the new tab
       * keeps a handle on this one via window.opener.
       */
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      // AttachmentUrlError already carries wording aimed at the reader — a record predating
      // working uploads says so, rather than opening a tab full of S3 error XML.
      setError(err instanceof AttachmentUrlError ? err.message : 'Could not open this attachment.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <>
      <ul className="pms-workspace__attachment-list">
        {attachments.map((att) => (
          <li key={att.id} className="pms-workspace__attachment-item">
            <button
              type="button"
              className="pms-workspace__attachment-name"
              onClick={() => handleOpen(att)}
              disabled={busyId === att.id}
              aria-label={`Download ${att.fileName}`}
            >
              📄 {att.fileName}
            </button>
            <span className="pms-workspace__attachment-meta">
              {busyId === att.id ? 'Opening…' : `${(att.fileSize / 1024).toFixed(1)} KB`}
            </span>
          </li>
        ))}
      </ul>
      {error && (
        <p className="pms-workspace__attachment-error" role="alert">{error}</p>
      )}
    </>
  );
};

const ReviewSnapshotObjectives: React.FC<{ review: Review; cycle?: ReviewCycle }> = ({
  review,
  cycle,
}) => {
  const ratings = review.objectiveRatings || [];

  /*
   * A High Level review has no per-objective final progress, evaluation or rating.
   *
   * The manager's own table already hides those three columns for a HIGH_LEVEL cycle, and the
   * manager is never given a control to set them — handleSubmitFinalReview drops the
   * performanceRating requirement entirely for high-level, and isObjectiveReviewedByManager asks
   * only for a comment. So the fields genuinely do not exist for this review type.
   *
   * This table, however, took only the review and had no way to know the type, so it printed
   * Final Progress, Evaluation and Rating regardless. On a High Level review that produced a
   * Rating column that could never say anything but "Not rated" — a column reporting the absence
   * of a value that was never collected. Passing the cycle lets both sides apply one rule.
   */
  const isHighLevelReview = cycle?.reviewCycleType === ReviewCycleType.HIGH_LEVEL;

  if (ratings.length === 0) {
    return <p className="pms-review-flow__empty">No objective snapshot was submitted with this review.</p>;
  }

  return (
    <div className="pms-review-flow__obj-table-wrap">
      <table className="pms-review-flow__obj-table">
        <thead>
          <tr>
            <th>Objective</th>
            {/* Both figures, side by side. The employee's self-assessment used to be shown
                under the "Final Progress" heading because they shared one field — so the
                employee's number was labelled as the manager's verdict on it. */}
            <th>Self-Assessed</th>
            {/*
              * What the employee WROTE, next to the figure they gave.
              *
              * It was collected and stored all along and had nowhere to appear: the self
              * review asks for a note per objective, and this table showed the number and
              * then jumped straight to the manager’s verdict. An employee reading their own
              * completed review could not see the case they had made for it, and a manager
              * opening it saw a percentage with no reasoning attached.
              *
              * "Self", not "Your": this same table is what the manager reads.
              */}
            <th>Self Comment</th>
            {!isHighLevelReview && <th>Final Progress</th>}
            {!isHighLevelReview && <th>Evaluation</th>}
            <th>Manager Comment</th>
            {!isHighLevelReview && <th>Rating</th>}
          </tr>
        </thead>
        <tbody>
          {ratings.map((rating) => (
            <tr key={rating.objectiveId}>
              <td className="pms-review-flow__obj-title-cell"><strong>{rating.objectiveTitle}</strong></td>
              <td>{employeeProgressOf(rating)}%</td>
              {/* employeeCommentOf, not `rating.comments`: on a record written before the
                  employee and manager comments were split apart, that field holds the
                  MANAGER’s text. See its note. */}
              <td className="pms-review-flow__obj-comment-cell">
                {employeeCommentOf(rating, review) || 'Not provided'}
              </td>
              {!isHighLevelReview && <td>{rating.finalProgressPercentage}%</td>}
              {!isHighLevelReview && <td>{rating.evaluationSummary || 'Not provided'}</td>}
              {/* managerCommentOf, never `managerFinalComment || comments`: the self-review form
                  writes the employee's own note into `comments`, so the raw fallback printed the
                  employee's words under this heading the moment they submitted — a manager
                  evaluation that had not happened. Empty until the manager submits. */}
              <td>{managerCommentOf(rating, review) || 'Not provided'}</td>
              {!isHighLevelReview && (
                <td><RatingDisplay rating={rating.rating || ''} compact /></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function getCycleForReview(review: Review, cycles: ReviewCycle[]): ReviewCycle | undefined {
  return cycles.find((cycle) => cycle.id === review.reviewCycleId);
}

function getReviewTypeLabel(review: Review, cycles: ReviewCycle[]): string {
  const cycle = getCycleForReview(review, cycles);
  if (!cycle?.reviewCycleType) return '';
  return REVIEW_CYCLE_TYPE_LABELS[cycle.reviewCycleType] || '';
}

function getReviewCycleName(review: Review, cycles: ReviewCycle[]): string {
  return review.reviewCycleName?.trim() || getCycleForReview(review, cycles)?.name || review.reviewCycleId || 'Unknown review cycle';
}

function getReviewTitle(review: Review, cycles: ReviewCycle[]): string {
  const metadata = review.reviewMetadata || {};
  return (
    review.name?.trim() ||
    review.reviewName?.trim() ||
    metadata.reviewName?.trim() ||
    metadata.name?.trim() ||
    getReviewCycleName(review, cycles)
  );
}

/**
 * Status shown in the manager's queue.
 *
 * Derived from the review phase and the two submitted-at timestamps rather than from
 * inferReviewWorkflowStatus, which only knew Draft/Submitted/Completed and so reported a
 * review the employee had finished as "Draft" — indistinguishable from one nobody had
 * touched, in the very list a manager uses to decide what to work on.
 *
 * Acknowledgement still wins over Manager Completed: it is a genuine later state. The old
 * `cycle.isLocked → 'Locked'` branch is gone because that flag locks *objectives*, which is
 * the precondition for reviewing rather than a reason the review is unavailable — review
 * availability is the review phase's job alone.
 */
function getReviewStatus(review: Review, cycles: ReviewCycle[]): string {
  const cycle = getCycleForReview(review, cycles);
  const status = deriveReviewStatus(cycle, review);
  if (review.isAcknowledged && status === ReviewStatus.MANAGER_COMPLETED) return 'Acknowledged';
  return status;
}

function formatTimelineDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTimelineTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getTimelineIcon(action: string): string {
  const normalized = action.toLowerCase();
  if (normalized.includes('created')) return '\u2726';
  if (normalized.includes('submitted') || normalized.includes('published')) return '\u25B2';
  if (normalized.includes('acknowledged')) return '\u2714';
  if (normalized.includes('reopened') || normalized.includes('reopen')) return '\u21BA';
  if (normalized.includes('locked')) return '\uD83D\uDD12';
  if (normalized.includes('unlocked')) return '\uD83D\uDD13';
  if (normalized.includes('draft')) return '\u25B7';
  if (normalized.includes('returned')) return '\u21A9';
  if (normalized.includes('approved')) return '\u2705';
  if (normalized.includes('comment')) return '\uD83D\uDCAC';
  return '\u2022';
}

function buildDefaultReviewHistory(review: Review, cycle?: ReviewCycle): ReviewHistoryEntry[] {
  if (review.reviewHistory && review.reviewHistory.length > 0) return review.reviewHistory;
  const metaHistory = review.reviewMetadata?.reviewHistory;
  if (metaHistory && metaHistory.length > 0) return metaHistory;

  const entries: ReviewHistoryEntry[] = [
    buildReviewHistoryEntry(
      ReviewHistoryAction.REVIEW_CREATED,
      review.reviewerId,
      review.reviewerName,
      undefined,
      'Review Created'
    ),
  ];
  if (review.workflowStatus === ReviewWorkflowStatus.SUBMITTED || review.workflowStatus === ReviewWorkflowStatus.COMPLETED) {
    entries.push(
      buildReviewHistoryEntry(
        review.reviewType === ReviewType.MID_YEAR
          ? ReviewHistoryAction.REVIEW_SUBMITTED
          : ReviewHistoryAction.REVIEW_PUBLISHED,
        review.reviewerId,
        review.reviewerName,
        undefined,
        `${getReviewWorkflowStatusLabel(review.workflowStatus)}`
      )
    );
  }
  if (review.isAcknowledged && review.acknowledgedAt) {
    entries.push(
      buildReviewHistoryEntry(
        ReviewHistoryAction.REVIEW_ACKNOWLEDGED,
        review.acknowledgedBy || review.employeeId,
        review.employeeName,
        undefined,
        'Review Acknowledged'
      )
    );
  }
  if (review.reviewMetadata?.reopenedAt) {
    entries.push(
      buildReviewHistoryEntry(
        ReviewHistoryAction.REVIEW_REOPENED,
        review.reviewMetadata.reopenedBy || 'hr-admin',
        review.reviewMetadata.reopenedByName || 'HR Admin',
        undefined,
        review.reviewMetadata.reopenReason || 'Review Reopened'
      )
    );
  }
  if (cycle?.isLocked) {
    entries.push(
      buildReviewHistoryEntry(
        ReviewHistoryAction.REVIEW_LOCKED,
        'hr-admin',
        'HR Admin',
        undefined,
        'Review cycle locked'
      )
    );
  }
  return entries;
}

/**
 * The five-point scale, as the manager picks it per objective.
 *
 * Same values and same wording as ReviewForm's RATING_OPTIONS — the two manager forms must offer
 * an identical scale or the same objective means different things depending on which screen it
 * was rated from.
 */
const OBJECTIVE_RATING_OPTIONS: { value: RatingScale; label: string }[] = [
  { value: RatingScale.OUTSTANDING, label: 'Outstanding (5)' },
  { value: RatingScale.EXCEEDS_EXPECTATIONS, label: 'Exceeds Expectations (4)' },
  { value: RatingScale.MEETS_EXPECTATIONS, label: 'Meets Expectations (3)' },
  { value: RatingScale.NEEDS_IMPROVEMENT, label: 'Needs Improvement (2)' },
  { value: RatingScale.UNSATISFACTORY, label: 'Unsatisfactory (1)' },
];

interface StarRatingControlProps {
  value?: RatingScale | '';
  onChange: (rating: RatingScale) => void;
  label: string;
  /**
   * Refuses input, visibly.
   *
   * The caller used to express this by making its own onChange a no-op —
   * `canEvaluate && allObjectivesReviewed && setPerformanceRating(r)` — so the stars rendered as
   * live controls and silently ate every click. A manager clicking a star and seeing nothing
   * happen reasonably concludes there is no rating control, which is what was reported.
   */
  disabled?: boolean;
  /** Shown beside the stars when disabled, so "why" is on screen rather than inferred. */
  disabledReason?: string;
}

const StarRatingControl: React.FC<StarRatingControlProps> = ({
  value, onChange, label, disabled = false, disabledReason,
}) => (
  <div className="pms-team-reviews__stars" role="radiogroup" aria-label={label} aria-disabled={disabled}>
    {[1, 2, 3, 4, 5].map((star) => {
      const rating = star as RatingScale;
      const active = Number(value || 0) >= star;
      return (
        <button
          key={star}
          type="button"
          className={`pms-team-reviews__star ${active ? 'pms-team-reviews__star--active' : ''}`}
          onClick={() => onChange(rating)}
          disabled={disabled}
          title={disabled && disabledReason ? disabledReason : `${getRatingLabel(rating)} (${rating})`}
          aria-label={`${getRatingLabel(rating)} (${rating})`}
          aria-checked={Number(value || 0) === star}
          role="radio"
        >
          {active ? '\u2605' : '\u2606'}
        </button>
      );
    })}
    {/* Requirement 4: the absence of a rating is stated, not left as five empty outlines. */}
    {!value && <span className="pms-team-reviews__stars-empty">Not rated</span>}
    {disabled && disabledReason && (
      <span className="pms-team-reviews__stars-hint">{disabledReason}</span>
    )}
  </div>
);

function getReviewSearchText(
  review: Review,
  cycles: ReviewCycle[],
  employeeNameOverride?: string
): string {
  return [
    employeeNameOverride,
    review.employeeName,
    getReviewTitle(review, cycles),
    review.employeeId,
    review.reviewerName,
    review.reviewerId,
    getReviewCycleName(review, cycles),
    getReviewStatus(review, cycles),
    review.overallComments,
    review.developmentSuggestions,
  ].filter(Boolean).join(' ').toLowerCase();
}

function filterReviewsBySearch(
  reviews: Review[],
  query: string,
  cycles: ReviewCycle[],
  employeeNamesByEmail: Record<string, string> = {}
): Review[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return reviews;
  return reviews.filter((review) => {
    const employeeNameOverride = employeeNamesByEmail[review.employeeId.toLowerCase()];
    return getReviewSearchText(review, cycles, employeeNameOverride).includes(normalized);
  });
}

const ReviewListSkeleton: React.FC = () => (
  <div className="pms-team-reviews__grid" aria-label="Loading reviews">
    {[0, 1, 2].map((item) => (
      <div key={item} className="pms-team-reviews__card pms-team-reviews__card--skeleton">
        <span />
        <span />
        <span />
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div className="pms-team-reviews__empty" role="status">
    <strong>{title}</strong>
    <span>{message}</span>
  </div>
);

// â”€â”€â”€ Collapsible Section (Accordion) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// â”€â”€â”€ Review History Timeline â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const ReviewHistoryTimeline: React.FC<{ review: Review; cycle?: ReviewCycle }> = ({ review, cycle }) => {
  const history = buildDefaultReviewHistory(review, cycle).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  const reviewTitle = getReviewTitle(review, cycle ? [cycle] : []);
  const cycleName = cycle?.name || getReviewCycleName(review, []) || 'Unknown review cycle';
  return (
    <div className="pms-team-reviews__timeline pms-team-reviews__timeline--vertical" role="list" aria-label="Review history">
      <h4 className="pms-team-reviews__timeline-title">{reviewTitle}</h4>
      {history.length === 0 ? (
        <p className="pms-team-reviews__timeline-empty">No history recorded yet.</p>
      ) : (
        <ol className="pms-team-reviews__timeline-list">
          {history.map((entry, index) => (
            <motion.li
              key={entry.id}
              className="pms-team-reviews__timeline-item"
              role="listitem"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.04 }}
            >
              <span className="pms-team-reviews__timeline-icon" aria-hidden="true">
                {getTimelineIcon(String(entry.action))}
              </span>
              <div className="pms-team-reviews__timeline-body">
                <p className="pms-team-reviews__timeline-action">{entry.action}</p>
                <p className="pms-team-reviews__timeline-user">{entry.performedByName}</p>
                <div className="pms-team-reviews__timeline-meta">
                  <time dateTime={entry.timestamp}>{formatTimelineDate(entry.timestamp)}</time>
                  <span>{formatTimelineTime(entry.timestamp)}</span>
                  <span>{cycleName}</span>
                </div>
              </div>
            </motion.li>
          ))}
        </ol>
      )}
    </div>
  );
};

export const ManagerProgressDashboard: React.FC<{ reviews: Review[] }> = ({ reviews }) => {
  const stats = computeManagerReviewProgress(reviews);
  return (
    <div className="pms-team-reviews__progress-dashboard" role="region" aria-label="Review progress">
      <div className="pms-team-reviews__progress-card">
        <span>Total Assigned</span>
        <strong>{stats.total}</strong>
      </div>
      <div className="pms-team-reviews__progress-card pms-team-reviews__progress-card--success">
        <span>Completed</span>
        <strong>{stats.completed} / {stats.total}</strong>
      </div>
      <div className="pms-team-reviews__progress-card">
        <span>In Progress</span>
        <strong>{stats.inProgress}</strong>
      </div>
      <div className="pms-team-reviews__progress-card">
        <span>Pending</span>
        <strong>{stats.pending}</strong>
      </div>
      <div className="pms-team-reviews__progress-card">
        <span>Submitted</span>
        <strong>{stats.submitted}</strong>
      </div>
    </div>
  );
};

// â”€â”€â”€ Manager View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface ManagerViewProps {
  reviews: Review[];
  loading: boolean;
  allCycles: ReviewCycle[];
  selectedCycleId: string;
  userId: string;
  userName: string;
  isSuperAdmin: boolean;
  autoSelectingReviewCycle: boolean;
  onReviewCreated: () => void;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
}

type TeamMember = { id: string; email: string; name: string };

const ManagerView: React.FC<ManagerViewProps> = ({
  reviews,
  loading,
  allCycles,
  selectedCycleId,
  userId,
  userName,
  isSuperAdmin,
  autoSelectingReviewCycle,
  onReviewCreated,
  panelOpen,
  setPanelOpen,
}) => {
  const renderStart = performance.now();
  console.log('[Perf][TeamReviews][ManagerView] render start');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  /**
   * Whether the user directory that scopes this screen actually loaded.
   *
   * Kept separate from `teamMembers.length` because zero members has two very different
   * meanings — see loadDirectReportsWithStatus.
   */
  const [directoryStatus, setDirectoryStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [directoryRetry, setDirectoryRetry] = useState(0);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [formCycleName, setFormCycleName] = useState('');
  /*
   * The cycle a new review will be created against.
   *
   * `useState(selectedCycleId)` alone captured the value only on ManagerView's FIRST render, and
   * this component stays mounted for the whole session — so switching the cycle dropdown at the
   * top of the page left formCycleId holding whatever was selected at mount. Creating "Review 3"
   * after switching to it posted `reviewCycleId` for Review 2, and the new record inherited every
   * cycle-derived thing about the closed review: its phase, its timeline, its lock state.
   *
   * Worse, it was silent. `<select value={formCycleId}>` falls back to rendering the first option
   * when the value matches none, so the panel showed the manager one cycle while the state held
   * another. The effect below keeps the two in step; see also the reset when the panel opens.
   */
  const [formCycleId, setFormCycleId] = useState(selectedCycleId);
  const [formDescription, setFormDescription] = useState('');
  const [creating, setCreating] = useState(false);
  /** Members skipped because they already have a review in the chosen cycle — see handleCreate. */
  const [createNotice, setCreateNotice] = useState('');

  /*
   * The cycles a new review may actually be created against.
   *
   * Narrower than `allCycles`, and deliberately local to this panel rather than applied to the
   * page: allCycles still has to contain the closed Review 2 so the manager can open and read it.
   * It is only as a *target for a new review* that a closed cycle must not be offered.
   *
   * canAcceptNewReview is the rule, in pmsWorkflow, because filterActiveCycles cannot express it:
   * that one gates on `status`, and HR's close writes `reviewPhase` and leaves status ACTIVE.
   */
  const assignableCycles = useMemo(
    () => allCycles.filter(canAcceptNewReview),
    [allCycles]
  );

  /*
   * Follow the page's cycle selection, and never hold an id that is not on offer.
   *
   * Two failure modes, one effect. The manager switching cycles must move the create target with
   * them; and if the cycle they are viewing cannot take a new review (HR has closed it), the form
   * must fall to one that can rather than keeping an id the dropdown does not list — which is the
   * state that rendered one cycle and submitted another.
   */
  // A notice from the previous attempt must not greet the manager on the next one.
  useEffect(() => {
    if (panelOpen) setCreateNotice('');
  }, [panelOpen]);

  useEffect(() => {
    setFormCycleId((current) => {
      const preferred = assignableCycles.some((cycle) => cycle.id === selectedCycleId)
        ? selectedCycleId
        : '';
      if (preferred) return preferred;
      // Selected cycle cannot take one: keep the current choice only if it is still assignable.
      if (current && assignableCycles.some((cycle) => cycle.id === current)) return current;
      return assignableCycles[0]?.id || '';
    });
  }, [selectedCycleId, assignableCycles]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [reviewSearchText, setReviewSearchText] = useState('');
  const [memberSearchText, setMemberSearchText] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<TeamMember | null>(null);
  const [selectedEmployeeReviews, setSelectedEmployeeReviews] = useState<Review[]>([]);
  const [employeeReviewsLoading, setEmployeeReviewsLoading] = useState(false);
  const [employeeObjectives, setEmployeeObjectives] = useState<Objective[]>([]);
  const [objectivesLoading, setObjectivesLoading] = useState(false);
  const [performanceRating, setPerformanceRating] = useState<RatingScale | ''>('');
  /*
   * The manager's per-objective rating.
   *
   * The getter was discarded — `const [, setObjectiveRatings] = …` — so this state was reset and
   * pre-filled but never read and never rendered. That is the whole reason the manager had no
   * rating control: the write side was wired up and the control itself was never built.
   * ReviewForm, the other manager form, has had a per-objective Rating select all along, and the
   * employee's Rating column reads ObjectiveRating.rating — so this is the value that column has
   * been waiting for.
   */
  const [objectiveRatings, setObjectiveRatings] = useState<Record<string, RatingScale | ''>>({});
  const [objectiveComments, setObjectiveComments] = useState<Record<string, string>>({});
  const [objectiveSummaries, setObjectiveSummaries] = useState<Record<string, ObjectiveEvaluationSummary | ''>>({});
  const [objectiveFinalProgress, setObjectiveFinalProgress] = useState<Record<string, number | ''>>({});
  /*
   * Whether the manager has actually ENTERED a Final Progress figure, as opposed to the slider
   * merely holding one.
   *
   * The slider is deliberately seeded with the employee's self-assessed percentage — that is the
   * number the manager is there to agree or disagree with, and opening at 0 would be worse. But
   * the seeded value is a real number, so `objectiveFinalProgress[id] !== ''` reported it as the
   * manager's answer, and an objective the manager had never touched counted as reviewed. Having
   * the value and having answered are two different facts and now have two different flags.
   */
  const [objectiveProgressEntered, setObjectiveProgressEntered] = useState<Record<string, boolean>>({});
  const [reviewNotes, setReviewNotes] = useState('');
  const [devSuggestions, setDevSuggestions] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [employeeSelectorOpen, setEmployeeSelectorOpen] = useState(false);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<'objectives' | 'feedback' | 'discussion' | 'timeline' | 'attachments'>('objectives');
  const [postingMessage, setPostingMessage] = useState(false);
  const [discussionError, setDiscussionError] = useState('');

  /*
   * The employee's attachments, for the manager to read before evaluating.
   *
   * The manager view simply never had this tab — the employee view has had one all along, and
   * the records were there the whole time. Nothing was mis-keyed or filtered out; there was no
   * query on this side at all.
   *
   * The parent type really is OBJECTIVE with the *review* id as the key. That pairing looks
   * wrong but it is what the employee's upload writes, so reading with anything else — the
   * seemingly-correct AttachmentParentType.REVIEW included — finds nothing. Matching the
   * stored key exactly is the whole point; this is not the place to correct that naming.
   */
  const [managerAttachments, setManagerAttachments] = useState<Attachment[]>([]);
  const [managerAttachmentsLoading, setManagerAttachmentsLoading] = useState(false);
  const [managerAttachmentsError, setManagerAttachmentsError] = useState('');

  useEffect(() => {
    if (!selectedReview) {
      setManagerAttachments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setManagerAttachmentsLoading(true);
      setManagerAttachmentsError('');
      try {
        const found = await GraphQLPMSService.getAttachments(
          AttachmentParentType.OBJECTIVE,
          selectedReview.id
        );
        if (!cancelled) setManagerAttachments(found);
      } catch (err) {
        // Distinguished from "none uploaded" on purpose: an empty state shown after a failed
        // query tells the manager there is no evidence when there may well be.
        if (!cancelled) setManagerAttachmentsError('Could not load attachments.');
      } finally {
        if (!cancelled) setManagerAttachmentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedReview?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const detailRef = React.useRef<HTMLDivElement>(null);
  const selectedCycle = allCycles.find((c) => c.id === selectedCycleId);
  const teamMemberEmails = useMemo(
    () => new Set(teamMembers.map((member) => member.email.trim().toLowerCase())),
    [teamMembers]
  );
  // Rule lives in pmsWorkflow (isReviewInManagerScope) so the cycle isolation and the
  // reviewer fallback are covered by tests rather than only by clicking through the screen.
  const selectedCycleReviews = useMemo(
    () => reviews.filter((review) =>
      isReviewInManagerScope(review, {
        selectedCycleId,
        teamMemberEmails,
        managerId: userId,
        isSuperAdmin,
      })
    ),
    [isSuperAdmin, reviews, selectedCycleId, teamMemberEmails, userId]
  );
  const reviewMetrics = useMemo(() => {
    const pending = selectedCycleReviews.filter((review) => {
      const status = inferReviewWorkflowStatus(review);
      return status === ReviewWorkflowStatus.DRAFT ||
        status === ReviewWorkflowStatus.IN_PROGRESS ||
        status === ReviewWorkflowStatus.REOPEN_REQUESTED;
    }).length;
    const completed = selectedCycleReviews.filter((review) => {
      const status = inferReviewWorkflowStatus(review);
      return status === ReviewWorkflowStatus.SUBMITTED || status === ReviewWorkflowStatus.COMPLETED;
    }).length;
    return { total: selectedCycleReviews.length, pending, completed };
  }, [selectedCycleReviews]);
  /**
   * Who the tiles count.
   *
   * Normally the team roster. When the directory is unavailable the roster is empty, and
   * counting against it reported 0/0/0/0 next to a review list that plainly had rows in it —
   * so fall back to the employees named on the reviews actually held. The tiles then describe
   * real records instead of contradicting the list beneath them.
   */
  const progressEmails = useMemo(() => {
    if (teamMembers.length > 0) return teamMembers.map((m) => m.email);
    return Array.from(new Set(selectedCycleReviews.map((r) => r.employeeId)));
  }, [teamMembers, selectedCycleReviews]);
  /** Headline counts for the tiles — per team member, from the submitted-at timestamps. */
  const teamProgress = useMemo(
    () => computeTeamReviewProgress(progressEmails, selectedCycleReviews),
    [progressEmails, selectedCycleReviews]
  );
  const reviewEditable = isReviewEditable(selectedReview, selectedCycle);
  /**
   * The sequence gate, separate from reviewEditable on purpose.
   *
   * reviewEditable answers "is this record still open for writes at all" (cycle lock,
   * workflowStatus) and also decides whether the objectives tab renders the live table or the
   * submitted snapshot. It has never known about employeeSubmittedAt, so a review sitting at
   * Waiting Employee was fully writable by the manager: Final Progress, Evaluation and Manager
   * Comment were all live, and Submit Review persisted them. canEvaluate adds the missing
   * dependency without touching what reviewEditable governs, so the manager still *sees* the
   * objectives and the employee's own numbers — the controls are disabled and the reason is
   * stated, rather than the data disappearing.
   */
  const managerBlockReason = getManagerReviewBlockReason(
    selectedCycle,
    selectedReview,
    selectedEmployee?.name
  );
  const canEvaluate = reviewEditable && !managerBlockReason;
  const isHighLevelReview = selectedCycle?.reviewCycleType === ReviewCycleType.HIGH_LEVEL;
  /**
   * How many objectives the MANAGER has evaluated.
   *
   * Every input to this used to be pre-filled from something the manager had not written — the
   * employee's note into the comment box, the employee's self-assessed percentage into the
   * slider, and a previous review's verdict from the shared Objective record into the evaluation
   * dropdown. So a review the manager had never opened reported "Reviewed Objectives 2/2" with a
   * green tick on every row, beside a Manager Completed count of 0: two parts of one screen
   * disagreeing about the same fact.
   *
   * The prefills are fixed at source, and `objectiveProgressEntered` now separates a seeded
   * slider value from an answer. isObjectiveReviewedByManager is shared with the per-row tick so
   * the count and the ticks cannot drift apart again.
   */
  const isObjectiveReviewedByManager = useCallback((objectiveId: string): boolean => {
    const hasComment = !!objectiveComments[objectiveId]?.trim();
    // A High Level review collects an interim comment and nothing else — no slider, no dropdown.
    if (isHighLevelReview) return hasComment;
    const hasFinalProgress = !!objectiveProgressEntered[objectiveId]
      && objectiveFinalProgress[objectiveId] !== ''
      && objectiveFinalProgress[objectiveId] !== undefined;
    // Rating included to match ReviewForm's Year End validation, which has always required a
    // per-objective rating alongside the comment and the evaluation summary. This form collected
    // no rating at all, so "fully reviewed" meant something weaker here than on the other
    // manager form for the same review type.
    return hasFinalProgress
      && !!objectiveSummaries[objectiveId]
      && !!objectiveRatings[objectiveId]
      && hasComment;
  }, [objectiveComments, objectiveSummaries, objectiveRatings, objectiveFinalProgress, objectiveProgressEntered, isHighLevelReview]);

  const objectiveProgress = useMemo(() => ({
    reviewed: employeeObjectives.filter((obj) => isObjectiveReviewedByManager(obj.id)).length,
    total: employeeObjectives.length,
  }), [employeeObjectives, isObjectiveReviewedByManager]);

  const computeSuggestedRating = (): number | undefined => {
    // With Year End reviews, the overall rating is a single manager decision (1-5 stars)
    // No per-objective rating to compute from
    return performanceRating ? Number(performanceRating) : undefined;
  };

  const filteredMembers = useMemo(() => {
    const q = memberSearchText.toLowerCase();
    return teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(q) ||
        member.email.toLowerCase().includes(q)
    );
  }, [teamMembers, memberSearchText]);

  const handleSelectEmployee = (member: TeamMember) => {
    setSelectedEmployee(member);
    setReviewSearchText(member.name);
    setEmployeeSelectorOpen(false);
    setSelectedReview(null);
    setSelectedEmployeeReviews([]);
    setPerformanceRating('');
    setObjectiveRatings({});
    setObjectiveComments({});
    setObjectiveSummaries({});
    setObjectiveFinalProgress({});
    setObjectiveProgressEntered({});
    setReviewNotes('');
    setDevSuggestions('');
  };

  const handleSelectReview = async (review: Review) => {
    setSelectedReview(review);
    const snapshot = await loadReviewSnapshot(review, 'manager');
    setSelectedReview((current) => current?.id === review.id ? snapshot : current);
    setPerformanceRating(snapshot.overallRating || '');
    setReviewNotes(snapshot.overallComments || '');
    setDevSuggestions(snapshot.developmentSuggestions || '');
  };

  /*
   * Post a discussion message as the manager.
   *
   * Writes ONLY reviewMetadata.discussion. Deliberately independent of reviewNotes /
   * devSuggestions / objectiveComments — replying in the thread must never be mistaken for, or
   * write into, the formal evaluation. Existing metadata is spread back in because updateReview
   * serialises the whole object.
   */
  const handlePostDiscussionMessage = async (text: string) => {
    if (!selectedReview) return;
    setPostingMessage(true);
    setDiscussionError('');
    try {
      const message = buildReviewDiscussionMessage(userId, userName, 'manager', text);
      const result = await GraphQLPMSService.updateReview(selectedReview.id, {
        reviewMetadata: {
          ...(selectedReview.reviewMetadata || {}),
          discussion: appendReviewDiscussion(selectedReview, message),
        },
      });
      if (!result.success || !result.entity) {
        throw new Error(result.errors?.[0] || 'Failed to post message');
      }
      setSelectedReview(result.entity);
    } catch (err: any) {
      console.error('[TeamReviews][ManagerView] Failed to post discussion message:', err);
      setDiscussionError('Could not send your message. Please try again.');
      throw err;
    } finally {
      setPostingMessage(false);
    }
  };

  useEffect(() => {
    if (selectedReview && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedReview]);

  useEffect(() => {
    if (!selectedEmployee) {
      setSelectedEmployeeReviews([]);
      setSelectedReview(null);
      setEmployeeObjectives([]);
      return;
    }
    setEmployeeReviewsLoading(true);
    /*
     * sameIdentity, not a lowercased ===. A review's employeeId is the address that was
     * current when it was raised, and everyone here owns two accounts on two domains, so an
     * exact match dropped every review filed under the employee's other address — the manager
     * saw an empty review list for someone who had submitted.
     */
    /*
     * One card per employee+cycle pair, canonical record kept.
     *
     * Without the dedupe the manager saw the same review twice with contradictory statuses —
     * "pms new re review 1 / Waiting Manager" beside "pms new re review 1 / Waiting Employee" —
     * because a duplicate pair carries different submission timestamps and each derives its own
     * status. The cause is fixed at both creation paths; this keeps the screen honest for pairs
     * already in the data. Nothing is deleted: the stale record is simply not the one shown.
     */
    const sortedReviews = dedupeReviewsByEmployeeCycle(
      selectedCycleReviews
        .filter((review) => sameIdentity(review.employeeId, selectedEmployee.email))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    );
    setSelectedEmployeeReviews(sortedReviews);
    setEmployeeReviewsLoading(false);
  }, [selectedEmployee, selectedCycleReviews]);

  /*
   * Reviews the manager can actually act on, separated from those still with the employee.
   *
   * The workflow layer already refuses manager evaluation before the employee submits —
   * canManagerReview requires SELF_SUBMITTED, and the workspace disables its fields and prints
   * getManagerReviewBlockReason. What it did not do was stop the queue from *presenting* those
   * reviews as work: one flat "Reviews" list where a row reading "Waiting Employee" sat beside
   * one genuinely awaiting evaluation, identical in weight. A manager reading that list cannot
   * tell what is theirs to do.
   *
   * Partitioned on canManagerReview rather than on employeeSubmittedAt directly, so this cannot
   * drift from the rule that gates the evaluation itself — a returned review carries a
   * submitted-at timestamp but is the employee's turn again, and only the shared helper knows
   * that. Nothing is hidden: the waiting rows are still listed, still openable for reading, and
   * still say whose turn it is.
   */
  const [actionableReviews, waitingOnEmployeeReviews] = useMemo(() => {
    const actionable: Review[] = [];
    const waiting: Review[] = [];
    selectedEmployeeReviews.forEach((review) => {
      const cycle = getCycleForReview(review, allCycles);
      // Manager-submitted and closed reviews are not pending manager work, but they are not
      // waiting on the employee either — they belong with the finished ones, i.e. actionable
      // in the sense of "the manager's side of this is done or in progress", never in the
      // "waiting for the employee" bucket.
      const isEmployeesTurn =
        getSelfReviewProgress(review) === SelfReviewProgress.NOT_STARTED ||
        getSelfReviewProgress(review) === SelfReviewProgress.RETURNED;
      if (canManagerReview(cycle, review)) actionable.push(review);
      else if (isEmployeesTurn) waiting.push(review);
      else actionable.push(review);
    });
    return [actionable, waiting];
  }, [selectedEmployeeReviews, allCycles]);

  useEffect(() => {
    if (!selectedReview || !selectedEmployeeReviews.some((review) => review.id === selectedReview.id)) {
      setSelectedReview(null);
      setPerformanceRating('');
      setObjectiveRatings({});
      setObjectiveFinalProgress({});
      setObjectiveProgressEntered({});
      setReviewNotes('');
      setDevSuggestions('');
    }
  }, [selectedEmployeeReviews, selectedReview]);

  useEffect(() => {
    if (!selectedEmployee || !selectedReview) {
      setEmployeeObjectives([]);
      return;
    }
    if (!reviewEditable) {
      setEmployeeObjectives([]);
      setObjectivesLoading(false);
      return;
    }
    (async () => {
      setObjectivesLoading(true);
      try {
        // Objectives belong to the parent Annual Cycle, not the child review cycle.
        // The selectedCycleId is a child review cycle (e.g., "Year End Review"),
        // but objectives are stored under the parent annual cycle.
        const parentCycleId = selectedCycle?.annualCycleId || selectedCycleId;
        console.log('[TeamReviews][ObjectiveLoad] Attempting load:', {
          employeeEmail: selectedEmployee.email,
          selectedCycleId,
          selectedCycleName: selectedCycle?.name,
          selectedCycleAnnualCycleId: selectedCycle?.annualCycleId,
          parentCycleId,
          reviewId: selectedReview?.id,
          reviewCycleId: selectedReview?.reviewCycleId,
        });

        // Use the same employee objective source as My Objectives and Team Objectives.
        // Review ratings are snapshots and must not replace the current objective record.
        const allObjectives = await GraphQLPMSService.getEmployeeObjectives(selectedEmployee.email);
        const sourceObjectivesById = new Map(allObjectives.map((objective) => [objective.id, objective]));
        const linkedObjectiveIds = Array.from(new Set(
          selectedReview.objectiveRatings
            .map((rating) => rating.objectiveId)
            .filter(Boolean)
        ));
        const cycleObjectives = allObjectives.filter(
          (objective) => String((objective as any).objectiveCycleId || objective.reviewCycleId || '') === parentCycleId
        );
        const objectives = cycleObjectives;
        const unmatchedReviewObjectiveIds = linkedObjectiveIds.filter(
          (objectiveId) => !sourceObjectivesById.has(objectiveId)
        );

        console.log('[TeamReviews][ObjectiveLoad] Resolved objective source:', {
          allObjectiveCount: allObjectives.length,
          linkedObjectiveIds,
          unmatchedReviewObjectiveIds,
          parentCycleId,
          removedOtherCycleObjectives: allObjectives.length - cycleObjectives.length,
          resolvedObjectiveCount: objectives.length,
        });
        objectives.forEach((objective) => {
          const objectiveId = objective.id;
          const reviewObjective = selectedReview.objectiveRatings.find(
            (rating) => rating.objectiveId === objectiveId
          );
          const sourceObjective = sourceObjectivesById.get(objectiveId);
          const resolvedProgressPercentage = objective.progressPercentage;

          console.log('[TeamReviews][ObjectiveProgressTrace]', {
            objectiveId,
            weightage: objective.weightage,
            'objective.progressPercentage': objective.progressPercentage,
            resolvedProgressPercentage,
            reviewObjective,
            sourceObjective,
          });
        });

        setEmployeeObjectives(objectives);
        const nextRatings: Record<string, RatingScale | ''> = {};
        const nextComments: Record<string, string> = {};
        const nextSummaries: Record<string, ObjectiveEvaluationSummary | ''> = {};
        const nextFinalProgress: Record<string, number | ''> = {};
        const nextProgressEntered: Record<string, boolean> = {};
        objectives.forEach((objective) => {
          const existing = selectedReview.objectiveRatings.find((rating) => rating.objectiveId === objective.id);
          // evaluationSummary is manager-only — the self review never writes one — so it is the
          // honest marker for "this objective carries a manager evaluation".
          const managerHasEvaluatedObjective = Boolean(existing?.evaluationSummary);
          /*
           * Gated on the manager having evaluated, exactly like the comment and the evaluation.
           *
           * `existing?.rating` is dual-written: the self-review form puts the employee's own
           * per-objective rating there for a Rating-tracked objective. Seeding the manager's
           * control from it would hand them the employee's judgement to submit as their own —
           * the same defect as the comment box, one field over.
           */
          nextRatings[objective.id] = managerHasEvaluatedObjective ? (existing?.rating || '') : '';
          /*
           * The manager's comment and evaluation seed from THIS review's rating, or from nothing.
           *
           * Two sources were removed, and both were leaks:
           *
           *  - `existing?.comments`, the employee's own per-objective self-review note. Handing it
           *    to the manager pre-filled meant a Save Draft persisted it into managerFinalComment,
           *    at which point it became indistinguishable from a real manager comment. That is why
           *    tightening managerCommentOf alone did not fix this: the data was already wrong.
           *    managerCommentDraftOf rejects a stored value identical to the employee's note.
           *
           *  - `objective.managerFinalComment` / `objective.evaluationSummary`, from the Objective
           *    record. Objectives hang off the *annual* cycle, so one Objective row is shared by
           *    Review 1, Review 2 and Review 3 alike — and persistReview writes the manager's
           *    verdict onto it on submit. Reading it back here pulled a previous review's manager
           *    evaluation straight into a brand-new one, across the review-cycle boundary the rest
           *    of this module works to keep. A review's manager evaluation belongs to that review.
           */
          nextComments[objective.id] = existing
            ? managerCommentDraftOf(existing, selectedReview)
            : '';
          nextSummaries[objective.id] = existing?.evaluationSummary || '';
          /*
           * Seed the manager's Final Progress from their own previous figure only once they
           * have actually evaluated this objective.
           *
           * The self review writes finalProgressPercentage: 0 as a placeholder — the field is
           * the manager's and the employee must not seed it. But `existing?.finalProgressPercentage ?? …`
           * treats that placeholder 0 as a real manager answer, so the slider opened at 0 on a
           * review the manager had never touched. Leaving it there saved 0 as the manager's
           * verdict, which is the second half of the "0% / 0%" completed review.
           *
           * evaluationSummary is the honest marker: buildReviewRatings always writes one when
           * the manager saves, and the self review never writes one. Before that, start from
           * the employee's own self-assessment — the number the manager is there to agree or
           * disagree with.
           */
          const managerHasEvaluated = managerHasEvaluatedObjective;
          nextFinalProgress[objective.id] = managerHasEvaluated
            ? (existing!.finalProgressPercentage ?? '')
            : (existing ? employeeProgressOf(existing) : objective.progressPercentage ?? '');
          // Seeded values are not answers. Only a figure the manager already committed counts.
          nextProgressEntered[objective.id] = managerHasEvaluated;
        });
        setObjectiveRatings(nextRatings);
        setObjectiveComments(nextComments);
        setObjectiveSummaries(nextSummaries);
        setObjectiveFinalProgress(nextFinalProgress);
        setObjectiveProgressEntered(nextProgressEntered);
      } finally {
        setObjectivesLoading(false);
      }
    })();
  }, [selectedEmployee, selectedReview, selectedCycleId, selectedCycle, reviewEditable]);

  // Overall rating is set manually by manager (1-5 stars) â€” not auto-calculated from objectives

  /*
   * The manager's evaluation is layered onto what the employee submitted, not built over it.
   *
   * This used to construct each ObjectiveRating from scratch out of employeeObjectives, so
   * every field the employee had written — their self-assessed progress, their own comment —
   * was silently discarded the moment the manager saved. The employee's self-assessment had
   * nowhere else to live, so it was simply gone.
   *
   * Starting from the stored rating and overriding only the manager-owned fields keeps the two
   * sides independent: employeeProgressPercentage and the employee's `comments` are carried
   * through untouched, while finalProgressPercentage, managerFinalComment and evaluationSummary
   * are the manager's to set.
   */
  /*
   * Return to Employee.
   *
   * Gated on canReturnToEmployee, which delegates to canManagerReview — so the button obeys
   * exactly the same rule as Submit Review and the two cannot drift apart. The reason is
   * mandatory and validated here as well as in the service, so an empty one never reaches the
   * network.
   */
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReasonDraft, setReturnReasonDraft] = useState('');
  const [returningReview, setReturningReview] = useState(false);
  const canReturn = canReturnToEmployee(selectedCycle, selectedReview);

  const handleReturnToEmployee = async () => {
    if (!selectedReview || !returnReasonDraft.trim()) return;
    setReturningReview(true);
    setSubmissionError('');
    try {
      const result = await GraphQLPMSService.returnSelfReviewToEmployee(
        selectedReview,
        returnReasonDraft,
        { id: userId, name: userName }
      );
      if (result.success) {
        setReturnDialogOpen(false);
        setReturnReasonDraft('');
        // Same refresh path the manager submit uses, so the status tiles, the card badge and
        // the disabled state all move together.
        onReviewCreated?.();
      } else {
        setSubmissionError(result.errors[0] || 'Could not return the review.');
      }
    } finally {
      setReturningReview(false);
    }
  };

  const buildReviewRatings = () => {
    const submitted = new Map(
      (selectedReview?.objectiveRatings || []).map((rating) => [rating.objectiveId, rating])
    );
    return enrichObjectiveRatings(
      employeeObjectives.map((objective) => {
        const employeeSubmitted = submitted.get(objective.id);
        return {
          ...employeeSubmitted,
          objectiveId: objective.id,
          objectiveTitle: objective.title,
          /*
           * The per-objective rating is whatever was already on the record — never the overall.
           *
           * This read `rating: performanceRating as RatingScale`, which stamped the manager's
           * single overall star rating onto every objective as if it were a per-objective
           * judgement they had made one by one. On a High Level review, where the overall rating
           * control is not rendered at all, `performanceRating` is `''` — so the cast wrote an
           * empty string into every objective and the Rating column read "Not rated" on a review
           * marked Manager Completed. That is the reported symptom.
           *
           * The same mistake the self-review form already carries a note about: an overall figure
           * spread across objectives is a judgement nobody made. This UI collects no per-objective
           * rating (see computeSuggestedRating — Year End is a single manager decision), so there
           * is nothing to write here beyond what the employee's self-review already put on a
           * Rating-tracked objective.
           */
          /*
           * The manager's rating for this objective, which is what the employee's Rating column
           * renders. Previously this passed the employee's self-rating straight through, because
           * no manager rating was ever collected — so that column could never leave "Not rated"
           * however the manager filled the form in.
           *
           * Left undefined when unset rather than defaulted: an unrated objective is a real
           * state, and the weighted-rating calculator skips absent ratings.
           */
          rating: (objectiveRatings[objective.id] || undefined) as RatingScale | undefined,
          finalProgressPercentage: objectiveFinalProgress[objective.id] !== '' && objectiveFinalProgress[objective.id] !== undefined
            ? Number(objectiveFinalProgress[objective.id])
            : (objective.finalProgressPercentage ?? objective.progressPercentage),
          // The employee's own comment stays on `comments`; the manager's goes to
          // managerFinalComment. Writing both from the manager's box, as before, meant the
          // employee's comment was overwritten by the manager's.
          comments: employeeSubmitted?.comments ?? '',
          managerFinalComment: objectiveComments[objective.id] || reviewNotes,
          /*
           * The manager's explicit selection, or nothing.
           *
           * This used to fall back to `ObjectiveEvaluationSummary.PARTIALLY_ACHIEVED` — a literal
           * constant, not a value derived from progress — so an objective the manager never
           * evaluated was recorded as Partially Achieved on their behalf. It is why two objectives
           * at 60% and 85% both read "Partially Achieved": the value had nothing to do with either
           * number. On a High Level review the Evaluation dropdown is not rendered at all, so
           * every objective took the fabricated value; and once a draft save had written it, the
           * form reopened pre-filled from the stored value and the "all objectives evaluated" gate
           * accepted the fabrication as a real selection.
           *
           * Left undefined instead. The Evaluation column already renders 'Not provided' for an
           * absent value, which is the true statement.
           */
          evaluationSummary: objectiveSummaries[objective.id] || undefined,
        };
      })
    );
  };

  const persistReview = async (
    workflowStatus: ReviewWorkflowStatus,
    options: { finalizeObjectives?: boolean; notifyEmployee?: boolean } = {}
  ) => {
    if (!selectedEmployee || !selectedReview) return;
    setSavingReview(true);
    setSubmissionError('');
    try {
      const reviewRatings = buildReviewRatings();
      const historyEntry = buildReviewHistoryEntry(
        workflowStatus === ReviewWorkflowStatus.SUBMITTED
          ? ReviewHistoryAction.REVIEW_SUBMITTED
          : ReviewHistoryAction.DRAFT_SAVED,
        userId,
        userName,
        selectedReview.workflowStatus,
        workflowStatus
      );
      const reviewHistory = appendReviewHistory(selectedReview, historyEntry);
      const reviewMetadata = {
        ...(selectedReview.reviewMetadata || {}),
        workflowStatus,
        reviewHistory,
        submittedAt:
          workflowStatus === ReviewWorkflowStatus.SUBMITTED
            ? new Date().toISOString()
            : selectedReview.submittedAt,
        publishedAt:
          workflowStatus === ReviewWorkflowStatus.SUBMITTED
            ? new Date().toISOString()
            : selectedReview.publishedAt,
      };

      const result = await GraphQLPMSService.updateReview(selectedReview.id, {
        objectiveRatings: reviewRatings,
        overallRating: performanceRating || undefined,
        suggestedRating: computeSuggestedRating(),
        overallComments: reviewNotes,
        developmentSuggestions: devSuggestions,
        workflowStatus,
        reviewMetadata,
        /*
         * The timestamp the whole derived model keys off.
         *
         * getSelfReviewProgress, deriveReviewStatus, computeTeamReviewProgress and the review
         * timeline all read managerSubmittedAt, and this path never wrote it — only the
         * ManagerReviewFormWrapper route did. So a review submitted from Team Reviews stayed at
         * "Waiting Manager" forever, the Manager Completed tile never moved off zero, and the
         * "Manager Submitted" timeline step never lit up. Only set on the final submit: a
         * draft save is not a submission, and writing it there would lock the manager out of
         * their own unfinished draft via canManagerReview.
         */
        ...(workflowStatus === ReviewWorkflowStatus.SUBMITTED
          ? { managerSubmittedAt: selectedReview.managerSubmittedAt || new Date().toISOString() }
          : {}),
      });

      if (!result.success || !result.entity) {
        setSubmissionError(result.errors.join(' ') || 'Failed to save review');
        return;
      }

      if (options.finalizeObjectives) {
        await Promise.all(
          reviewRatings.map((rating) => {
            const objective = employeeObjectives.find((item) => item.id === rating.objectiveId);
            if (!objective) return Promise.resolve();
            return GraphQLPMSService.updateObjective(
              objective.id,
              {
                finalProgressPercentage: rating.finalProgressPercentage,
                // Never `|| rating.comments`: that is the employee's self-review note, and this
                // writes to the shared Objective record — which the next review cycle's manager
                // form then reads back. One empty manager box used to seed every later review
                // with the employee's words.
                managerFinalComment: rating.managerFinalComment,
                evaluationSummary: rating.evaluationSummary,
                status: ObjectiveStatus.COMPLETED,
              },
              {
                action: 'Review Submitted',
                changedBy: userId,
                changedByName: userName,
                previousObjective: objective,
              }
            );
          })
        );
      }

      if (options.notifyEmployee && selectedEmployee.email) {
        const cycleName = getReviewCycleName(result.entity, allCycles);
        if (selectedReview.reviewType === ReviewType.MID_YEAR) {
          await notifyMidYearReviewSubmitted(selectedEmployee.email, userName, cycleName);
        } else if (selectedReview.reviewType === ReviewType.QUARTERLY) {
          await notifyHighLevelReviewPublished(selectedEmployee.email, userName, cycleName);
        } else {
          await notifyFinalReviewAvailable(selectedEmployee.email, userName, cycleName);
        }
      }

      setSelectedReview(result.entity);
      setToastMessage(workflowStatus === ReviewWorkflowStatus.SUBMITTED ? 'Review submitted successfully' : 'Draft saved');
      setTimeout(() => setToastMessage(''), 3000);
      onReviewCreated();
    } finally {
      setSavingReview(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!canEvaluate) return;
    const nextStatus =
      objectiveProgress.reviewed > 0
        ? ReviewWorkflowStatus.IN_PROGRESS
        : ReviewWorkflowStatus.DRAFT;
    await persistReview(nextStatus);
  };

  const handleSubmitFinalReview = async () => {
    if (!selectedEmployee || (!isHighLevelReview && !performanceRating) || !canEvaluate) return;
    // Validate all objectives are reviewed (final progress + evaluation + comment)
    if (objectiveProgress.reviewed < objectiveProgress.total) {
      // The fourth copy of this rule, now the same one the count, the ticks and the banner use.
      // Four copies is how the tally and the ticks came to disagree in the first place.
      const pending = employeeObjectives.filter((obj) => !isObjectiveReviewedByManager(obj.id));
      setSubmissionError(
        pending.map((o) => `Objective "${o.title}" has not been fully reviewed.`).join(' ')
      );
      return;
    }
    await persistReview(ReviewWorkflowStatus.SUBMITTED, {
      finalizeObjectives: true,
      notifyEmployee: true,
    });
  };

  const handleRequestReopen = async () => {
    if (!selectedReview || !reopenReason.trim()) return;
    const historyEntry = buildReviewHistoryEntry(
      ReviewHistoryAction.REOPEN_REQUESTED,
      userId,
      userName,
      selectedReview.workflowStatus,
      reopenReason.trim()
    );
    const reviewHistory = appendReviewHistory(selectedReview, historyEntry);
    const reviewMetadata = {
      ...(selectedReview.reviewMetadata || {}),
      workflowStatus: ReviewWorkflowStatus.REOPEN_REQUESTED,
      reviewHistory,
      reopenRequest: {
        reason: reopenReason.trim(),
        requestedBy: userId,
        requestedByName: userName,
        requestedAt: new Date().toISOString(),
        status: 'Pending' as const,
      },
    };
    const result = await GraphQLPMSService.updateReview(selectedReview.id, {
      workflowStatus: ReviewWorkflowStatus.REOPEN_REQUESTED,
      reviewMetadata,
    });
    if (result.success && result.entity) {
      setSelectedReview(result.entity);
      setReopenReason('');
      onReviewCreated();
    }
  };

  // Load review candidates. SUPER_ADMIN sees global active users; managers see direct reports.
  useEffect(() => {
    (async () => {
      const startedAt = performance.now();
      const queryName = isSuperAdmin ? 'loadAllActiveEmployees' : 'loadDirectReports';
      console.log('[Perf][TeamReviews][ManagerView] API start candidates', { userId, queryName });
      try {
        /*
         * directoryAvailable is the point of this call.
         *
         * Every count on this screen and the review list itself are scoped through
         * teamMembers, and the old code could not tell a failed directory read from a manager
         * with no reports — both arrived as []. When it was the former the screen reported
         * Employees: 0 and "No review records for <cycle>" even though the cycle's reviews had
         * loaded successfully, which is the inconsistency being fixed.
         */
        const { reports, directoryAvailable } = isSuperAdmin
          ? await loadAllActiveEmployees(userId).then(r => ({
              reports: r,
              directoryAvailable: r.length > 0,
            }))
          : await loadDirectReportsWithStatus(userId);
        console.log('[Perf][TeamReviews][ManagerView] API complete candidates', {
          durationMs: Math.round(performance.now() - startedAt),
          reportCount: reports.length,
          directoryAvailable,
          queryName,
        });
        setTeamMembers(
          reports.map(r => ({
            id: r.email,
            email: r.email,
            name: r.name,
          }))
        );
        setDirectoryStatus(directoryAvailable ? 'ready' : 'error');
      } catch (err) {
        console.error('[TeamReviews] Failed to load team members:', err);
        setTeamMembers([]);
        setDirectoryStatus('error');
      }
    })();
  }, [userId, isSuperAdmin, directoryRetry]);

  // Sync form cycle with selected cycle
  useEffect(() => {
    setFormCycleId(selectedCycleId);
  }, [selectedCycleId]);

  useEffect(() => {
    console.log('[Perf][TeamReviews][ManagerView] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      loading,
      reviews: reviews.length,
      teamMembers: teamMembers.length,
      selectedReviewId: selectedReview?.id,
    });
  });

  useEffect(() => {
    const displayedReviewIds = selectedEmployeeReviews.map((review) => review.id);
    const diagnostics = {
      selectedReviewCycleId: selectedCycleId,
      selectedReviewCycleName: selectedCycle?.name || 'Unknown cycle',
      totalReviewRecordsFound: reviewMetrics.total,
      pendingReviewCount: reviewMetrics.pending,
      completedReviewCount: reviewMetrics.completed,
      teamMemberCount: teamMembers.length,
      reviewIdsReturned: selectedCycleReviews.map((review) => review.id),
      selectedEmployeeReviewIdsDisplayed: displayedReviewIds,
      selectedEmployeeListCount: displayedReviewIds.length,
      metricReconciliation: reviewMetrics.pending + reviewMetrics.completed === reviewMetrics.total,
      datasource: 'selected-cycle reviews from GraphQLPMSService.getReviewsByReviewCycle',
    };
    console.groupCollapsed('[TeamReviews][Metric Diagnostics]');
    console.info(diagnostics);
    console.table(selectedCycleReviews.map((review) => ({
      id: review.id,
      employeeId: review.employeeId,
      reviewerId: review.reviewerId,
      reviewCycleId: review.reviewCycleId,
      workflowStatus: inferReviewWorkflowStatus(review),
    })));
    if (!diagnostics.metricReconciliation) {
      console.error('Team Review metric cards do not reconcile.', diagnostics);
    }
    console.groupEnd();
  }, [reviewMetrics, selectedCycle?.name, selectedCycleId, selectedCycleReviews, selectedEmployeeReviews, teamMembers.length]);

  const handleSelectAll = () => {
    if (selectedMembers.length === teamMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(teamMembers.map((m) => m.email));
    }
  };

  const handleToggleMember = (email: string) => {
    setSelectedMembers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const handleCreate = async () => {
    if (!formCycleId || selectedMembers.length === 0) return;
    setCreating(true);
    setCreateNotice('');
    try {
      const selectedReviewCycle = allCycles.find((cycle) => cycle.id === formCycleId);
      /*
       * Refuse a target the form should never have been holding.
       *
       * Belt to the dropdown's braces: assignableCycles already excludes closed cycles, but this
       * is the last point before a record is written against one, and the whole defect was the
       * form's id disagreeing with what was on screen. A closed cycle here means the guard above
       * was bypassed, not that the manager chose one.
       */
      if (!selectedReviewCycle || !canAcceptNewReview(selectedReviewCycle)) {
        setCreateNotice('That review cycle is closed and cannot receive new reviews. Pick an open one.');
        return;
      }
      const selectedReviewCycleName = selectedReviewCycle?.name || formCycleId;
      const reviewName = formCycleName.trim() || selectedReviewCycleName;
      // Shared derivation — see resolveReviewTypeForCycle. Inlining it here is what let this
      // path and submitSelfReview disagree and create two reviews for one employee+cycle.
      const selectedReviewType = resolveReviewTypeForCycle(selectedReviewCycle);
      /*
       * Skip anyone who already has a review in THIS cycle — and skip them, never update them.
       *
       * handleCreate previously created unconditionally, so pressing Create twice, or creating for
       * a member who already had one, produced a second Review row for the same employee and cycle.
       * Every reader then resolved the pair by taking the first match — `reviews.find(r =>
       * r.reviewCycleId === selectedCycleId)`, submitSelfReview's `existing` lookup, the manager's
       * queue — so the new record existed while the screen kept showing the old completed one.
       * That is the "new review shows the previous review's state" report, and no amount of
       * changing the displayed status would have touched it.
       *
       * Deliberately a skip and not an update: an existing review is somebody's submitted work,
       * and resetting it to Draft to satisfy a create action would destroy it. Keyed on
       * employee + reviewCycleId + reviewType via findReviewForCycle — never on the annual cycle,
       * which is shared by Review 1, 2 and 3 alike and cannot tell them apart.
       */
      const existingForCycle = await GraphQLPMSService.getReviewsByReviewCycle(formCycleId);
      /*
       * Keyed on employee + cycle WITHOUT reviewType. Passing selectedReviewType meant a record
       * the employee had created by submitting (which used a different reviewType) was invisible
       * to this check, so Create Review happily added a second one.
       */
      const alreadyHasReview = (empEmail: string) => !!resolveCanonicalReview(
        existingForCycle, empEmail, formCycleId
      );
      const membersToCreate = selectedMembers.filter((empEmail) => !alreadyHasReview(empEmail));
      const skipped = selectedMembers.filter(alreadyHasReview);

      if (membersToCreate.length === 0) {
        setCreateNotice(
          `Every selected member already has a review in ${selectedReviewCycleName}. Nothing was created.`
        );
        return;
      }

      // Create a review for each selected team member
      await Promise.all(
        membersToCreate.map((empEmail) => {
          const member = teamMembers.find((m) => m.email === empEmail);
          const historyEntry = buildReviewHistoryEntry(
            ReviewHistoryAction.REVIEW_CREATED,
            userId,
            userName
          );
          const input = {
            name: reviewName,
            reviewName,
            employeeId: empEmail,
            reviewCycleId: formCycleId,
            reviewCycleName: selectedReviewCycleName,
            reviewType: selectedReviewType,
            objectiveRatings: [],
            overallRating: 0 as any,
            overallComments: formDescription || formCycleName,
            developmentSuggestions: '',
            workflowStatus: ReviewWorkflowStatus.DRAFT,
            reviewMetadata: {
              name: reviewName,
              reviewName,
              workflowStatus: ReviewWorkflowStatus.DRAFT,
              reviewCycleName: selectedReviewCycleName,
              reviewHistory: [historyEntry],
            },
          };
          return GraphQLPMSService.createReview(
            { ...input, employeeId: empEmail },
            userId,
            userName
          ).then((res) => {
            if (res.entity && member) {
              return GraphQLPMSService.updateReview(res.entity.id, {
                reviewMetadata: input.reviewMetadata,
                workflowStatus: ReviewWorkflowStatus.DRAFT,
              });
            }
            return res;
          });
        })
      );
      // Reset form
      setFormCycleName('');
      setFormDescription('');
      setSelectedMembers([]);
      setPanelOpen(false);
      // Notify the employees a review was actually created for — not the ones skipped, who would
      // otherwise be told they had been assigned a review that already existed.
      await Promise.all(
        membersToCreate.map((empEmail) =>
          notifyReviewAssigned(empEmail, userName, selectedReviewCycleName)
        )
      );
      if (skipped.length > 0) {
        setCreateNotice(
          `${membersToCreate.length} review(s) created. ${skipped.length} skipped — already had a review in ${selectedReviewCycleName}.`
        );
      }
      onReviewCreated();
    } catch (err) {
      console.error('[TeamReviews] Create review error:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="pms-toast">{toastMessage}</div>
      )}

      {/* Progressive Flow Layout */}
      <div className="pms-review-flow">
        {/*
          Surfaced rather than swallowed. Every count and the review list are scoped through the
          user directory, so when it fails the screen used to look like an empty review cycle.
        */}
        {directoryStatus === 'error' && (
          <div className="pms-review-flow__directory-error" role="alert">
            <div>
              <strong>Team directory unavailable.</strong>
              <span>
                Reviews assigned to you are still listed below, but team-wide counts and the
                member picker need the directory. Retry, or reload if it persists.
              </span>
            </div>
            <button
              type="button"
              className="pms-team-reviews__draft-btn"
              onClick={() => { setDirectoryStatus('loading'); setDirectoryRetry((k) => k + 1); }}
            >
              Retry
            </button>
          </div>
        )}
        {/* Summary Stats */}
        <div className="pms-review-flow__stats">
          {/*
            Four tiles, same markup and classes as before — only what they count has changed.
            Pending/Completed came from inferReviewWorkflowStatus, which cannot tell a finished
            self-review awaiting the manager apart from one the employee never started, and
            counted nothing at all for a member with no review record. These three states are
            derived from the submitted-at timestamps per team member, so they always sum to
            Employees. See computeTeamReviewProgress.
          */}
          <div className="pms-review-flow__stat">
            <span className="pms-review-flow__stat-value">{teamProgress.employees}</span>
            <span className="pms-review-flow__stat-label">Employees</span>
          </div>
          <div className="pms-review-flow__stat">
            <span className="pms-review-flow__stat-value">{teamProgress.waitingEmployee}</span>
            <span className="pms-review-flow__stat-label">Waiting Employee</span>
          </div>
          <div className="pms-review-flow__stat">
            <span className="pms-review-flow__stat-value">{teamProgress.selfReviewSubmitted}</span>
            <span className="pms-review-flow__stat-label">Self Review Submitted</span>
          </div>
          <div className="pms-review-flow__stat">
            <span className="pms-review-flow__stat-value">{teamProgress.managerCompleted}</span>
            <span className="pms-review-flow__stat-label">Manager Completed</span>
          </div>
        </div>

        {teamMembers.length > 0 && (
          <ReviewProgressSummary
            reviews={reviews}
            totalEmployees={teamMembers.length}
            title={selectedCycle ? `Self/Manager Review Progress — ${selectedCycle.name}` : 'Self/Manager Review Progress'}
          />
        )}

        {!loading && !autoSelectingReviewCycle && reviewMetrics.total === 0 && (
          <div className="pms-review-flow__cycle-empty" role="status">
            {/*
              Says which layer is actually empty. The previous copy asserted "Team members are
              available" unconditionally, so the one case that most needed explaining — the
              directory having failed, leaving nothing to scope the reviews against — was
              reported as the cycle having no records.
            */}
            <strong>No review records for {selectedCycle?.name || 'the selected cycle'}.</strong>
            <span>
              {directoryStatus === 'error'
                ? `The team directory did not load, so reviews for cycle ${selectedCycleId || 'not selected'} could not be matched to your team. Retry above before concluding the cycle is empty.`
                : teamMembers.length === 0
                  ? 'You have no direct reports on record, so there is no one to show reviews for. Ask HR to check reporting lines if that looks wrong.'
                  : `${teamMembers.length} team member${teamMembers.length === 1 ? '' : 's'} loaded, but no reviews were returned for cycle ID ${selectedCycleId || 'not selected'}. Select another review cycle or create reviews for this period.`}
            </span>
          </div>
        )}

        {/* Step 1: Employee Search */}
        <div className="pms-review-flow__search-section">
          <div className="pms-review-flow__search-wrapper">
            <input
              className="pms-review-flow__search"
              type="search"
              value={reviewSearchText}
              onFocus={() => setEmployeeSelectorOpen(true)}
              onBlur={() => setTimeout(() => setEmployeeSelectorOpen(false), 150)}
              onChange={(e) => {
                setReviewSearchText(e.target.value);
                setSelectedEmployee(null);
                setSelectedReview(null);
                setEmployeeSelectorOpen(true);
              }}
              placeholder="Search employee to review..."
              aria-label="Search employees"
            />
            {employeeSelectorOpen && (
              <div className="pms-review-flow__dropdown">
                {(reviewSearchText.trim()
                  ? teamMembers.filter(m => m.name.toLowerCase().includes(reviewSearchText.toLowerCase()) || m.email.toLowerCase().includes(reviewSearchText.toLowerCase()))
                  : teamMembers
                ).map((member) => (
                  <button
                    key={member.email}
                    className="pms-review-flow__dropdown-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectEmployee(member)}
                  >
                    <span className="pms-review-flow__dropdown-name">{member.name}</span>
                    <span className="pms-review-flow__dropdown-email">{member.email}</span>
                    {(() => {
                      // sameIdentity for the same reason as the review list above. With an
                      // exact match, a self-review filed under the member's other domain
                      // address was never found, so this row read "Waiting for employee"
                      // permanently and the manager's review stayed gated shut.
                      const memberReview = reviews.find((r) => sameIdentity(r.employeeId, member.email));
                      const progress = getSelfReviewProgress(memberReview);
                      if (progress === SelfReviewProgress.NOT_STARTED) {
                        return <span className="pms-review-flow__dropdown-status pms-review-flow__dropdown-status--waiting">⏳ Waiting for employee</span>;
                      }
                      if (progress === SelfReviewProgress.SELF_SUBMITTED) {
                        return (
                          <span className="pms-review-flow__dropdown-status pms-review-flow__dropdown-status--ready">
                            ✅ Self review submitted{memberReview?.employeeSubmittedAt ? ` · ${formatRelativeTime(memberReview.employeeSubmittedAt)}` : ''}
                          </span>
                        );
                      }
                      return (
                        <span className="pms-review-flow__dropdown-status pms-review-flow__dropdown-status--done">
                          ✅ Manager review completed{memberReview?.managerSubmittedAt ? ` · ${formatRelativeTime(memberReview.managerSubmittedAt)}` : ''}
                        </span>
                      );
                    })()}
                  </button>
                ))}
                {teamMembers.length === 0 && (
                  <p className="pms-review-flow__dropdown-empty">No team members found.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Employee Selected — Show summary + reviews */}
        {selectedEmployee && (
          <div className="pms-review-flow__employee-section">
            <div className="pms-review-flow__employee-card">
              <div className="pms-review-flow__employee-avatar">{selectedEmployee.name.charAt(0).toUpperCase()}</div>
              <div className="pms-review-flow__employee-info">
                <strong>{selectedEmployee.name}</strong>
                <span>{selectedEmployee.email}</span>
                <span className="pms-review-flow__employee-meta">{selectedEmployeeReviews.length} of {reviewMetrics.total} team review{reviewMetrics.total !== 1 ? 's' : ''} this cycle</span>
              </div>
              <button className="pms-review-flow__clear-btn" onClick={() => { setSelectedEmployee(null); setSelectedReview(null); setReviewSearchText(''); }}>✕</button>
            </div>

            {/* Reviews for this employee */}
            <div className="pms-review-flow__reviews-section">
              <h3 className="pms-review-flow__section-title">Reviews</h3>
              {employeeReviewsLoading ? (
                <p className="pms-review-flow__loading">Loading reviews...</p>
              ) : selectedEmployeeReviews.length === 0 ? (
                <p className="pms-review-flow__empty">No review record exists for {selectedEmployee.name} in {selectedCycle?.name || 'the selected cycle'} ({selectedCycleId}).</p>
              ) : (
                <>
                  {actionableReviews.length > 0 && (
                    <div className="pms-review-flow__reviews-list">
                      {actionableReviews.map((review) => (
                        <button
                          key={review.id}
                          className={`pms-review-flow__review-card ${selectedReview?.id === review.id ? 'pms-review-flow__review-card--active' : ''}`}
                          onClick={() => handleSelectReview(review)}
                        >
                          <span className="pms-review-flow__review-name">{getReviewTitle(review, allCycles)}</span>
                          <span className="pms-review-flow__review-type">{getReviewCycleName(review, allCycles).toUpperCase()}</span>
                          <span className="pms-review-flow__review-status">{getReviewStatus(review, allCycles)}</span>
                          <span className="pms-review-flow__review-date">{new Date(review.updatedAt || review.createdAt).toLocaleDateString()}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/*
                    Listed, but under its own heading and visually quieter — the manager owes
                    nothing here yet. Still openable, so they can read the objectives and see
                    for themselves what the employee has not sent.
                  */}
                  {waitingOnEmployeeReviews.length > 0 && (
                    <div className="pms-review-flow__waiting-group">
                      <h4 className="pms-review-flow__group-title">
                        Waiting on {selectedEmployee.name.split(' ')[0]} — no action needed from you
                      </h4>
                      <div className="pms-review-flow__reviews-list">
                        {waitingOnEmployeeReviews.map((review) => (
                          <button
                            key={review.id}
                            className={`pms-review-flow__review-card pms-review-flow__review-card--waiting ${selectedReview?.id === review.id ? 'pms-review-flow__review-card--active' : ''}`}
                            onClick={() => handleSelectReview(review)}
                          >
                            <span className="pms-review-flow__review-name">{getReviewTitle(review, allCycles)}</span>
                            <span className="pms-review-flow__review-type">{getReviewCycleName(review, allCycles).toUpperCase()}</span>
                            <span className="pms-review-flow__review-status">{getReviewStatus(review, allCycles)}</span>
                            <span className="pms-review-flow__review-date">{new Date(review.updatedAt || review.createdAt).toLocaleDateString()}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {actionableReviews.length === 0 && (
                    <p className="pms-review-flow__empty">
                      Nothing to evaluate for {selectedEmployee.name} yet — their self-review has
                      not been submitted.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Review Selected — Workspace */}
        {selectedReview && selectedEmployee && (
          <div className="pms-review-flow__workspace">
            {/* Workspace Header */}
            <div className="pms-review-flow__workspace-header">
              <div>
                <h3>{getReviewTitle(selectedReview, allCycles)}</h3>
                <span>{getReviewCycleName(selectedReview, allCycles)} • {getReviewStatus(selectedReview, allCycles)} • {selectedEmployee.name}</span>
              </div>
            </div>

            {/* Tab Bar */}
            <div className="pms-review-flow__tabs">
              <button className={`pms-review-flow__tab ${activeWorkspaceTab === 'objectives' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveWorkspaceTab('objectives')}>Objectives {(reviewEditable ? employeeObjectives.length : selectedReview.objectiveRatings.length) > 0 && <span className="pms-review-flow__tab-badge">{reviewEditable ? employeeObjectives.length : selectedReview.objectiveRatings.length}</span>}</button>
              <button className={`pms-review-flow__tab ${activeWorkspaceTab === 'feedback' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveWorkspaceTab('feedback')}>Feedback</button>
              <button className={`pms-review-flow__tab ${activeWorkspaceTab === 'discussion' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveWorkspaceTab('discussion')}>
                Discussion
                {getReviewDiscussion(selectedReview).length > 0 && <span className="pms-review-flow__tab-badge">{getReviewDiscussion(selectedReview).length}</span>}
              </button>
              <button className={`pms-review-flow__tab ${activeWorkspaceTab === 'timeline' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveWorkspaceTab('timeline')}>Timeline</button>
              <button className={`pms-review-flow__tab ${activeWorkspaceTab === 'attachments' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveWorkspaceTab('attachments')}>Attachments {managerAttachments.length > 0 && <span className="pms-review-flow__tab-badge">{managerAttachments.length}</span>}</button>
            </div>

            {/* Tab Content */}
            <div className="pms-review-flow__tab-content">
              {/* OBJECTIVES TAB */}
              {activeWorkspaceTab === 'objectives' && (
                !reviewEditable ? (
                  <ReviewSnapshotObjectives review={selectedReview} cycle={selectedCycle} />
                ) : (
                <div className="pms-review-flow__objectives">
                  {managerBlockReason && (
                    <div className="pms-team-reviews__sequence-gate" role="status">
                      <strong>Manager evaluation locked</strong>
                      <span>{managerBlockReason}</span>
                    </div>
                  )}
                  {employeeObjectives.length > 0 && (
                    <div className="pms-team-reviews__objective-progress" role="status">
                      <div className="pms-team-reviews__objective-progress-head">
                        <span>Reviewed Objectives</span>
                        <strong>{objectiveProgress.reviewed} / {objectiveProgress.total}</strong>
                      </div>
                      <div className="pms-team-reviews__objective-progress-bar" aria-hidden="true">
                        <span style={{ width: objectiveProgress.total ? `${Math.round((objectiveProgress.reviewed / objectiveProgress.total) * 100)}%` : '0%' }} />
                      </div>
                    </div>
                  )}
                  {objectivesLoading ? (
                    <p className="pms-review-flow__loading">Loading objectives...</p>
                  ) : employeeObjectives.length === 0 ? (
                    <p className="pms-review-flow__empty">No objectives found.</p>
                  ) : (
                    <div className="pms-review-flow__obj-table-wrap">
                      <table className="pms-review-flow__obj-table">
                        <thead>
                          <tr>
                            <th>Objective</th>
                            <th>Weightage</th>
                            <th>Current Progress</th>
                            {!isHighLevelReview && <th>Final Progress</th>}
                            {!isHighLevelReview && <th>Evaluation</th>}
                            {/* The column the manager had nowhere to fill in. Placed beside
                                Evaluation because the two are one judgement, and because this is
                                the value the employee's own Rating column reads back. */}
                            {!isHighLevelReview && <th>Rating</th>}
                            <th>Comment</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeObjectives.map((objective) => {
                            // The same predicate the count uses — the tick and the tally cannot
                            // disagree about whether a row has been evaluated.
                            const isObjReviewed = isObjectiveReviewedByManager(objective.id);
                            return (
                              <tr key={objective.id} className={`pms-review-flow__obj-row ${isObjReviewed ? 'pms-review-flow__obj-row--reviewed' : ''}`}>
                                <td className="pms-review-flow__obj-title-cell"><strong>{objective.title}</strong></td>
                                <td>{objective.weightage}%</td>
                                <td>
                                  <div className="gt-progress-cell">
                                    <ProgressBar value={objective.progressPercentage} size="md" />
                                  </div>
                                </td>
                                {!isHighLevelReview && (
                                  <td>
                                    {/*
                                      * The same control the Current Progress column beside it uses.
                                      *
                                      * This was a bare 70px <input type="range"> with a flat grey
                                      * track: no fill, so the value was legible only from the number
                                      * next to it, and 56px of travel for a hundred steps — half a
                                      * pixel per percent, which is not a control anyone can set a
                                      * figure with. Two cells presenting the same quantity, one as a
                                      * filled bar and one as a grey stub with a dot on it.
                                      *
                                      * ProgressSlider is what ProgressBar is built from, so the two
                                      * columns are now literally the same component, and the thumb
                                      * clamping, keyboard handling and aria labelling come with it.
                                      */}
                                    <div className="pms-review-flow__fp-cell">
                                      <ProgressSlider
                                        value={Number(objectiveFinalProgress[objective.id]) || 0}
                                        onChange={(next) => {
                                          setObjectiveFinalProgress((prev) => ({ ...prev, [objective.id]: next }));
                                          setObjectiveProgressEntered((prev) => ({ ...prev, [objective.id]: true }));
                                        }}
                                        disabled={!canEvaluate}
                                        size="sm"
                                        /*
                                         * The figure is rendered outside, not by the slider, because
                                         * "not entered yet" and "entered 0%" have to stay
                                         * distinguishable — objectiveProgressEntered gates whether the
                                         * row counts as reviewed, and a slider that always prints a
                                         * number would show 0% for a cell nobody has touched.
                                         */
                                        showPercentage={false}
                                        ariaLabel={`Final progress for ${objective.title}`}
                                      />
                                      <span>{objectiveFinalProgress[objective.id] !== '' ? `${objectiveFinalProgress[objective.id]}%` : '—'}</span>
                                    </div>
                                  </td>
                                )}
                                {!isHighLevelReview && (
                                  <td>
                                    <select className="pms-team-reviews__select" value={objectiveSummaries[objective.id] || ''} onChange={(e) => setObjectiveSummaries((prev) => ({ ...prev, [objective.id]: e.target.value as ObjectiveEvaluationSummary | '' }))} disabled={!canEvaluate}>
                                      <option value="">Select...</option>
                                      <option value={ObjectiveEvaluationSummary.GOAL_ACHIEVED}>Goal Achieved</option>
                                      <option value={ObjectiveEvaluationSummary.PARTIALLY_ACHIEVED}>Partially Achieved</option>
                                      <option value={ObjectiveEvaluationSummary.NOT_ACHIEVED}>Not Achieved</option>
                                    </select>
                                  </td>
                                )}
                                {!isHighLevelReview && (
                                  <td>
                                    <select
                                      className="pms-team-reviews__select"
                                      value={objectiveRatings[objective.id] || ''}
                                      onChange={(e) => setObjectiveRatings((prev) => ({
                                        ...prev,
                                        [objective.id]: e.target.value ? (Number(e.target.value) as RatingScale) : '',
                                      }))}
                                      disabled={!canEvaluate}
                                      aria-label={`Rating for ${objective.title}`}
                                    >
                                      {/* "Not rated" rather than "Select...": an unrated objective
                                          is a state the employee's view names, and the two screens
                                          should use one word for it. */}
                                      <option value="">Not rated</option>
                                      {OBJECTIVE_RATING_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </select>
                                  </td>
                                )}
                                <td>
                                  <input className="pms-team-reviews__table-input" value={objectiveComments[objective.id] || ''} onChange={(e) => setObjectiveComments((prev) => ({ ...prev, [objective.id]: e.target.value }))} placeholder={isHighLevelReview ? 'Interim comment...' : 'Manager comment...'} disabled={!canEvaluate} />
                                </td>
                                <td>
                                  <span className={`pms-team-reviews__obj-status ${isObjReviewed ? 'pms-team-reviews__obj-status--reviewed' : 'pms-team-reviews__obj-status--pending'}`}>
                                    {isObjReviewed ? '\u2713' : '\u25CB'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                )
              )}

              {/* FEEDBACK TAB */}
              {activeWorkspaceTab === 'feedback' && (
                <div className="pms-review-flow__feedback">
                  {managerBlockReason && (
                    <div className="pms-team-reviews__sequence-gate" role="status">
                      <strong>Manager evaluation locked</strong>
                      <span>{managerBlockReason}</span>
                    </div>
                  )}
                  {/* What the employee actually said, above the manager's own fields. The
                      manager had no way to read this before evaluating. */}
                  <EmployeeSelfReviewSummary review={selectedReview} audience="manager" />
                  {!isHighLevelReview && (
                    <div className="pms-review-flow__field">
                      <label>Overall Rating</label>
                      {/*
                        * onChange is now unconditional and the gate is a real disabled state.
                        * It used to read `canEvaluate && allReviewed && setPerformanceRating(r)`,
                        * which rendered live-looking stars that discarded every click — no state
                        * change, no message, no disabled styling. That is indistinguishable from a
                        * broken control, and it is why the rating was reported as unavailable.
                        */}
                      <StarRatingControl
                        value={performanceRating}
                        onChange={setPerformanceRating}
                        label="Overall performance rating"
                        disabled={!canEvaluate || objectiveProgress.reviewed < objectiveProgress.total}
                        disabledReason={
                          !canEvaluate
                            ? (managerBlockReason || 'This review is not open for evaluation.')
                            : `Rate each objective first — ${objectiveProgress.reviewed} of ${objectiveProgress.total} complete.`
                        }
                      />
                    </div>
                  )}
                  <div className="pms-review-flow__field">
                    <label>{isHighLevelReview ? 'Interim Comments / Performance Notes' : 'Overall Comments'}</label>
                    <textarea className="pms-team-reviews__textarea" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows={4} placeholder={isHighLevelReview ? 'Interim comments, performance notes, progress feedback...' : 'Overall assessment...'} disabled={!canEvaluate ||(!isHighLevelReview && objectiveProgress.reviewed < objectiveProgress.total)} />
                  </div>
                  {!isHighLevelReview && (
                    <div className="pms-review-flow__field">
                      <label>Development Suggestions</label>
                      <textarea className="pms-team-reviews__textarea" value={devSuggestions} onChange={(e) => setDevSuggestions(e.target.value)} rows={3} placeholder="Strengths, areas of improvement, training..." disabled={!canEvaluate ||objectiveProgress.reviewed < objectiveProgress.total} />
                    </div>
                  )}
                  {!isHighLevelReview && objectiveProgress.reviewed < objectiveProgress.total && canEvaluate && (
                    <div className="pms-team-reviews__validation-error" role="status">
                      <strong>Complete all objective evaluations before final review.</strong>
                      <br />Pending: {employeeObjectives.filter((obj) => !isObjectiveReviewedByManager(obj.id)).map(o => o.title).join(', ')}
                    </div>
                  )}
                  {submissionError && <div className="pms-team-reviews__validation-error" role="alert">{submissionError}</div>}
                  <div className="pms-team-reviews__review-actions">
                    {reviewEditable && (
                      <>
                        {/* Kept visible and disabled rather than removed, with the reason stated above. */}
                        <button className="pms-team-reviews__draft-btn" onClick={handleSaveDraft} disabled={!canEvaluate || savingReview} title={managerBlockReason || undefined}>{savingReview ? 'Saving...' : 'Save Draft'}</button>
                        {canReturn && (
                          <button
                            className="pms-team-reviews__draft-btn"
                            onClick={() => { setReturnReasonDraft(''); setReturnDialogOpen(true); }}
                            disabled={savingReview || returningReview}
                          >
                            Return to Employee
                          </button>
                        )}
                        <button className="pms-team-reviews__submit-btn" onClick={handleSubmitFinalReview} disabled={!canEvaluate || (!isHighLevelReview && !performanceRating) || savingReview || objectiveProgress.reviewed < objectiveProgress.total} title={managerBlockReason || undefined}>{savingReview ? 'Submitting...' : 'Submit Review'}</button>
                      </>
                    )}
                  </div>

                  {/* Reuses the PMS drawer rather than a bespoke modal, so the reason capture
                      looks like every other PMS form. */}
                  <RightSidePanel
                    isOpen={returnDialogOpen}
                    title="Return Review to Employee"
                    onClose={() => setReturnDialogOpen(false)}
                  >
                    <div className="pms-form-group">
                      <label className="pms-form-label" htmlFor="pms-return-reason">
                        Reason <span className="required">*</span>
                      </label>
                      <textarea
                        id="pms-return-reason"
                        className="pms-form-textarea"
                        value={returnReasonDraft}
                        onChange={(e) => setReturnReasonDraft(e.target.value)}
                        placeholder="Explain what needs to be reviewed or corrected..."
                        rows={5}
                        disabled={returningReview}
                      />
                      <p className="pms-form-hint">
                        The employee will be able to edit and resubmit their self review. Their
                        objectives stay locked.
                      </p>
                    </div>
                    <div className="pms-team-reviews__review-actions">
                      <button
                        className="pms-team-reviews__draft-btn"
                        onClick={() => setReturnDialogOpen(false)}
                        disabled={returningReview}
                      >
                        Cancel
                      </button>
                      <button
                        className="pms-team-reviews__submit-btn"
                        onClick={handleReturnToEmployee}
                        disabled={!returnReasonDraft.trim() || returningReview}
                        title={!returnReasonDraft.trim() ? 'A reason is required' : undefined}
                      >
                        {returningReview ? 'Returning...' : 'Return to Employee'}
                      </button>
                    </div>
                  </RightSidePanel>

                  {selectedReview && (selectedReview.workflowStatus === ReviewWorkflowStatus.SUBMITTED || selectedReview.workflowStatus === ReviewWorkflowStatus.COMPLETED) && (
                    <div className="pms-team-reviews__reopen-panel">
                      <label>Request Reopen</label>
                      <textarea className="pms-team-reviews__textarea" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={2} placeholder="Reason..." />
                      <button className="pms-team-reviews__draft-btn" onClick={handleRequestReopen} disabled={!reopenReason.trim() || savingReview}>Request Reopen</button>
                    </div>
                  )}
                </div>
              )}

              {activeWorkspaceTab === 'discussion' && (
                <ReviewDiscussion
                  messages={getReviewDiscussion(selectedReview)}
                  viewerRole="manager"
                  onSend={handlePostDiscussionMessage}
                  sending={postingMessage}
                  error={discussionError}
                  disabledReason={
                    selectedCycle?.isLocked
                      ? 'This review cycle is locked by HR, so the discussion is read-only.'
                      : undefined
                  }
                />
              )}

              {/* TIMELINE TAB */}
              {activeWorkspaceTab === 'timeline' && (
                <>
                  {/*
                    Two timelines, answering different questions. ReviewTimeline is the
                    seven-stage workflow position — objectives created through review closed,
                    with a timestamp per stage where one exists. ReviewHistoryTimeline below is
                    the audit trail of individual edits. This tab previously showed only the
                    latter, so there was nowhere in review details that stated how far along the
                    enterprise sequence the review actually was.
                  */}
                  <ReviewTimeline
                    cycle={selectedCycle}
                    review={selectedReview}
                    objectives={employeeObjectives}
                  />
                  <ReviewHistoryTimeline review={selectedReview} cycle={selectedCycle} />
                </>
              )}

              {/* Read-only by design: the employee owns these. No upload, no delete — the
                  employee's Add Attachment control stays the only way in or out. */}
              {activeWorkspaceTab === 'attachments' && (
                <div className="pms-workspace__attachments">
                  {managerAttachmentsLoading ? (
                    <p className="pms-workspace__no-content">Loading attachments...</p>
                  ) : managerAttachmentsError ? (
                    <p className="pms-workspace__no-content" role="alert">{managerAttachmentsError}</p>
                  ) : managerAttachments.length === 0 ? (
                    <p className="pms-workspace__no-content">No attachments added to this review.</p>
                  ) : (
                    <ReviewAttachmentList attachments={managerAttachments} />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Review Panel (existing) */}
      <RightSidePanel
        isOpen={panelOpen}
        title=""
        onClose={() => setPanelOpen(false)}
        wide
      >
        <div className="pms-create-review">
          <div className="pms-create-review__header">
            <div className="pms-create-review__header-text">
              <h2 className="pms-create-review__title">Create Review</h2>
              <p className="pms-create-review__subtitle">Create and assign a review cycle for team members</p>
            </div>
          </div>

          <div className="pms-create-review__body">
            <div className="pms-create-review__section">
              <label className="pms-create-review__label">Review Cycle Name</label>
              <input
                type="text"
                className="pms-create-review__input"
                placeholder="e.g. Q4 2024 Performance Review"
                value={formCycleName}
                onChange={(e) => setFormCycleName(e.target.value)}
              />
            </div>

            <div className="pms-create-review__section">
              <label className="pms-create-review__label">Review Cycle</label>
              <select
                className="pms-create-review__select"
                value={formCycleId}
                onChange={(e) => setFormCycleId(e.target.value)}
              >
                {assignableCycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name}
                  </option>
                ))}
              </select>
              {/* Stated rather than left as an empty dropdown: "no cycle is open" and "the list
                  failed to load" look identical otherwise, and the Create button is disabled
                  either way. */}
              {assignableCycles.length === 0 && (
                <p className="pms-create-review__hint" role="status">
                  No open review cycle is available. HR opens a review in Review Cycle
                  Configuration; a closed review cannot receive new reviews.
                </p>
              )}
            </div>

            <div className="pms-create-review__section">
              <label className="pms-create-review__label">Reporting Team Members</label>
              <div className="pms-create-review__members-card">
                <div className="pms-create-review__members-search">
                  <input
                    className="pms-create-review__input"
                    type="search"
                    placeholder="Search reporting members..."
                    value={memberSearchText}
                    onChange={(event) => setMemberSearchText(event.target.value)}
                    aria-label="Search reporting team members"
                  />
                </div>
                <div className="pms-create-review__members-list">
                  <label className="pms-create-review__member-row pms-create-review__member-row--all">
                    <input
                      type="checkbox"
                      checked={selectedMembers.length === teamMembers.length && teamMembers.length > 0}
                      onChange={handleSelectAll}
                    />
                    <span className="pms-create-review__member-all-label">Select All ({teamMembers.length})</span>
                  </label>
                  {filteredMembers.map((member) => (
                    <label key={member.email} className="pms-create-review__member-row">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.email)}
                        onChange={() => handleToggleMember(member.email)}
                      />
                      <div className="pms-create-review__member-avatar">
                        {member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </div>
                      <div className="pms-create-review__member-info">
                        <span className="pms-create-review__member-name">{member.name}</span>
                        <span className="pms-create-review__member-email">{member.email}</span>
                      </div>
                    </label>
                  ))}
                  {teamMembers.length === 0 && (
                    <p className="pms-create-review__no-members">No team members found.</p>
                  )}
                </div>
                {selectedMembers.length > 0 && (
                  <div className="pms-create-review__members-count">
                    {selectedMembers.length} of {teamMembers.length} selected
                  </div>
                )}
              </div>
            </div>

            <div className="pms-create-review__section">
              <label className="pms-create-review__label">Description</label>
              <textarea
                className="pms-create-review__textarea"
                placeholder="Optional description or notes for this review..."
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={5}
              />
            </div>
          </div>

          {/* Says what happened when nothing (or only some) was created. Without it a skipped
              member is indistinguishable from a silent failure — the panel just closed. */}
          {createNotice && (
            <p className="pms-create-review__notice" role="status">{createNotice}</p>
          )}

          <div className="pms-create-review__footer">
            <button
              className="pms-create-review__btn-cancel"
              onClick={() => setPanelOpen(false)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="pms-create-review__btn-primary"
              onClick={handleCreate}
              disabled={creating || selectedMembers.length === 0 || !formCycleId}
            >
              {creating ? 'Creating...' : `Create Review${selectedMembers.length > 0 ? ` (${selectedMembers.length})` : ''}`}
            </button>
          </div>
        </div>
      </RightSidePanel>
    </>
  );
};

// â”€â”€â”€ Employee View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface EmployeeViewProps {
  reviews: Review[];
  loading: boolean;
  userId: string;
  userName: string;
  allCycles: ReviewCycle[];
  selectedCycleId: string;
  onReviewSubmitted?: () => void;
}

const EmployeeView: React.FC<EmployeeViewProps> = ({ reviews, loading, userId, userName, allCycles, selectedCycleId, onReviewSubmitted }) => {
  const renderStart = performance.now();
  console.log('[Perf][TeamReviews][EmployeeView] render start');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  /** Upload/download failures, shown in the tab rather than only in the console. */
  const [attachmentError, setAttachmentError] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [refreshAttachments, setRefreshAttachments] = useState(0);
  const [reviewSearchText, setReviewSearchText] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);
  const [activeEmployeeTab, setActiveEmployeeTab] = useState<'objectives' | 'overview' | 'feedback' | 'discussion' | 'timeline' | 'attachments'>('objectives');
  const [postingMessage, setPostingMessage] = useState(false);
  const [discussionError, setDiscussionError] = useState('');
  const selectedCycle = allCycles.find((c) => c.id === selectedReview?.reviewCycleId);

  // ── Self review (employee's own entry point — separate from the review-viewing UI above) ──
  const activeCycle = allCycles.find((c) => c.id === selectedCycleId);
  /*
   * The employee's review for the active cycle — canonical, not merely first.
   *
   * "reviews.find(r => r.reviewCycleId === id)" returned whichever duplicate the API listed
   * first. When that was the manager-created twin (no employeeSubmittedAt) getSelfReviewProgress
   * read NOT_STARTED and canEditSelfReview said yes, so "Start Self Review" was still offered to
   * an employee who had already submitted — into the OTHER record. resolveCanonicalReview always
   * returns the furthest-progressed record, so the submission wins.
   */
  const myReviewForActiveCycle = resolveCanonicalReview(reviews, userId, selectedCycleId);
  const selfReviewProgress = getSelfReviewProgress(myReviewForActiveCycle);
  const canStartSelfReview = canEditSelfReview(activeCycle, myReviewForActiveCycle);
  const [selfReviewObjectives, setSelfReviewObjectives] = useState<Objective[]>([]);
  const [selfReviewPanelOpen, setSelfReviewPanelOpen] = useState(false);
  const [submittingSelfReview, setSubmittingSelfReview] = useState(false);

  /*
   * Objectives hang off the parent annual cycle, not the child review cycle.
   *
   * selectedCycleId here is a review cycle ("… review 1"); the employee's objectives are filed
   * under its parent objective cycle ("pms testing 1-1"). Passing the review cycle id straight
   * through matched nothing, so this list came back empty — and because buildReviewTimeline
   * derives "Objectives Submitted" from `objectives.every(submitted)` over an `objectives.length > 0`
   * guard, an empty list reads as *not submitted* however many objectives the employee actually
   * submitted. That is the My Reviews timeline showing Objectives Submitted outstanding while
   * Review Opened was already complete.
   *
   * The manager path above already resolves the parent this way; this is the same rule, and the
   * `objectiveCycleId || reviewCycleId` fallback matches how every other screen keys objectives
   * to a cycle.
   */
  useEffect(() => {
    if (!selectedCycleId) return;
    let cancelled = false;
    (async () => {
      const parentCycleId = activeCycle?.annualCycleId || selectedCycleId;
      const all = await GraphQLPMSService.getEmployeeObjectives(userId);
      if (cancelled) return;
      setSelfReviewObjectives(
        all.filter(
          objective =>
            String((objective as any).objectiveCycleId || objective.reviewCycleId || '') === parentCycleId
        )
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, selectedCycleId, activeCycle?.annualCycleId]);

  const handleSubmitSelfReview = async (submission: SelfReviewSubmission) => {
    setSubmittingSelfReview(true);
    try {
      /*
       * The reviewer, from the employee's current manager relationship first and the objectives
       * second.
       *
       * This used to be `selfReviewObjectives.find(o => o.managerId)` alone, and that is the
       * "Manager: Unassigned" defect. createObjective stores `managerId || 'unassigned'` and
       * `managerName || 'Unassigned'`, so an objective saved before its manager resolved carries
       * those literal strings — both truthy, so `.find(o => o.managerId)` selected it and the
       * review was stamped with the placeholder. It then rendered as the word "Unassigned",
       * straight past the `|| 'Not assigned'` guard on the Manager field, because a sentinel is
       * not falsy.
       *
       * The directory is consulted first because it is where the reporting line actually lives —
       * User Management writes custom:managerEmail, and the same source already decides who may
       * approve leave and edit a roster row. It also carries the manager's real *name*, which the
       * objective usually does not: ObjectiveForm has never passed one, so the stored
       * managerName is the placeholder even when the stored managerId is correct.
       *
       * The objectives stay as the fallback, so an employee whose directory record has no manager
       * set still gets the manager who owns the objectives being reviewed. resolveReviewerIdentity
       * discards placeholders from either source and takes the id and the name independently — see
       * its docstring for why those two halves must be allowed to come from different records.
       */
      const objectiveReviewer = selfReviewObjectives.find(
        (objective) => isResolvedReviewerValue(objective.managerId)
      );
      const directoryManagerEmail = (await getUserByEmail(userId))?.managerEmail;
      const directoryManager = isResolvedReviewerValue(directoryManagerEmail)
        ? await getUserByEmail(directoryManagerEmail as string)
        : undefined;

      const { reviewerId, reviewerName } = resolveReviewerIdentity([
        { id: directoryManagerEmail, name: directoryManager?.fullName || directoryManager?.name },
        { id: objectiveReviewer?.managerId, name: objectiveReviewer?.managerName },
      ]);

      const result = await GraphQLPMSService.submitSelfReview({
        employeeId: userId,
        employeeName: userName,
        reviewCycleId: selectedCycleId,
        // Shared derivation, so this can never disagree with the manager's create path again.
        reviewType: resolveReviewTypeForCycle(activeCycle),
        reviewerId,
        reviewerName,
        ...submission,
      });
      if (result.success) {
        setSelfReviewPanelOpen(false);
        onReviewSubmitted?.();
      }
    } finally {
      setSubmittingSelfReview(false);
    }
  };

  const handleSelectReview = async (review: Review) => {
    setSelectedReview(review);
    setActiveEmployeeTab('objectives');
    const snapshot = await loadReviewSnapshot(review, 'employee');
    setSelectedReview((current) => current?.id === review.id ? snapshot : current);
  };

  const handleAcknowledgeReview = async () => {
    if (!selectedReview || !canAcknowledgeReview(selectedReview)) return;
    setAcknowledging(true);
    try {
      const historyEntry = buildReviewHistoryEntry(
        ReviewHistoryAction.REVIEW_ACKNOWLEDGED,
        userId,
        userName,
        'Pending Acknowledgement',
        'Acknowledged'
      );
      const reviewHistory = appendReviewHistory(selectedReview, historyEntry);
      const acknowledgedAt = new Date().toISOString();
      const result = await GraphQLPMSService.updateReview(selectedReview.id, {
        isAcknowledged: true,
        acknowledgedAt,
        acknowledgedBy: userId,
        reviewMetadata: {
          ...(selectedReview.reviewMetadata || {}),
          reviewHistory,
        },
      });
      if (result.success && result.entity) {
        setSelectedReview(result.entity);
        await notifyReviewAcknowledged(
          selectedReview.reviewerId,
          userName,
          getReviewCycleName(result.entity, allCycles)
        );
      }
    } finally {
      setAcknowledging(false);
    }
  };

  const handleExportReview = () => {
    if (!selectedReview) return;
    ReportExportService.exportReviewSummaryPDF(selectedReview, selectedCycle);
  };

  /*
   * Post a discussion message.
   *
   * Writes ONLY reviewMetadata.discussion — never overallComments, never managerFinalComment,
   * never an ObjectiveRating. The existing metadata is spread back in because updateReview
   * serialises the whole object, so a partial write would drop reviewHistory/returnedAt.
   *
   * The updated review is put straight into state from the mutation result so the message shows
   * without a refetch; it is genuinely persisted, so a refresh shows the same thing.
   */
  const handlePostDiscussionMessage = async (text: string) => {
    if (!selectedReview) return;
    setPostingMessage(true);
    setDiscussionError('');
    try {
      const message = buildReviewDiscussionMessage(userId, userName, 'employee', text);
      const result = await GraphQLPMSService.updateReview(selectedReview.id, {
        reviewMetadata: {
          ...(selectedReview.reviewMetadata || {}),
          discussion: appendReviewDiscussion(selectedReview, message),
        },
      });
      if (!result.success || !result.entity) {
        throw new Error(result.errors?.[0] || 'Failed to post message');
      }
      setSelectedReview(result.entity);
    } catch (err: any) {
      console.error('[TeamReviews][EmployeeView] Failed to post discussion message:', err);
      setDiscussionError('Could not send your message. Please try again.');
      // Rethrow so ReviewDiscussion keeps the draft rather than clearing a message that
      // was never stored.
      throw err;
    } finally {
      setPostingMessage(false);
    }
  };

  // Load attachments when a review is selected
  useEffect(() => {
    if (!selectedReview) return;
    (async () => {
      const startedAt = performance.now();
      console.log('[Perf][TeamReviews][EmployeeView] API start selected review details', {
        reviewId: selectedReview.id,
        queryCount: 1,
      });
      setAttachmentsLoading(true);
      try {
        const atts = await GraphQLPMSService.getAttachments(
          AttachmentParentType.OBJECTIVE,
          selectedReview.id
        );
        console.log('[Perf][TeamReviews][EmployeeView] API complete selected review details', {
          durationMs: Math.round(performance.now() - startedAt),
          reviewId: selectedReview.id,
          attachmentCount: atts.length,
          queryCount: 1,
        });
        setAttachments(atts);
      } catch (err) {
        console.error('[TeamReviews] Failed to load attachments:', err);
      } finally {
        setAttachmentsLoading(false);
      }
    })();
  }, [selectedReview, refreshAttachments]);

  useEffect(() => {
    console.log('[Perf][TeamReviews][EmployeeView] render complete', {
      durationMs: Math.round(performance.now() - renderStart),
      loading,
      reviews: reviews.length,
      selectedReviewId: selectedReview?.id,
      attachments: attachments.length,
    });
  });

  /*
   * Upload the file, then record it — in that order.
   *
   * This used to synthesise `pms-reviews/<id>/attachments/<fileName>` into the s3Key column and
   * discard the `File` without ever calling S3. The row was real, so the count and the filename
   * showed correctly on both sides, but the key named an object that had never been written —
   * which is why opening it could not work no matter what the UI did. uploadReviewAttachmentFile
   * puts the bytes there first and returns the key that was actually used; a failed upload throws
   * and no record is created, rather than leaving another row pointing at nothing.
   */
  const handleAddAttachment = async () => {
    if (!selectedReview) return;
    // Create a file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const invalid = validateAttachmentFile(file);
      if (invalid) { setAttachmentError(invalid); return; }
      setAttachmentError('');
      setAttachmentUploading(true);
      try {
        const s3Key = await uploadReviewAttachmentFile(selectedReview.id, file);
        const result = await GraphQLPMSService.addAttachment(
          {
            // OBJECTIVE with the *review* id as the key. That pairing reads wrong but it is what
            // both read paths query on, so changing it here would orphan every existing record.
            parentType: AttachmentParentType.OBJECTIVE,
            parentId: selectedReview.id,
            fileName: file.name,
            fileType: file.type || file.name.split('.').pop() || '',
            fileSize: file.size,
            // The key the upload actually used, not a guess at one.
            s3Key,
          },
          userId
        );
        if (!result.success) throw new Error(result.errors[0] || 'Failed to record attachment');
        setRefreshAttachments((k) => k + 1);
      } catch (err) {
        console.error('[TeamReviews] Attachment upload error:', err);
        setAttachmentError('Upload failed. Please try again.');
      } finally {
        setAttachmentUploading(false);
      }
    };
    input.click();
  };

  /*
   * What an employee may see of their own review.
   *
   * The rule was "hide Draft and In_Progress", meant to keep a manager's half-written
   * evaluation away from the employee until it is submitted. But In_Progress is also the status
   * submitSelfReview writes when it *creates* the record — so the employee's own just-submitted
   * self-review was fetched correctly and then thrown away by this filter. The list fell back to
   * "No Reviews Found" while the banner, which reads the unfiltered `reviews` array, simultaneously
   * said "You submitted your self review" — the two disagreed because only one of them was
   * filtered.
   *
   * employeeSubmittedAt is the honest test for "is this the employee's own submitted work":
   * it is written by submitSelfReview and by nothing else, so it cannot be confused with the
   * manager's progress. The original intent is preserved — a manager draft on a review the
   * employee has not submitted still has no employeeSubmittedAt and stays hidden.
   */
  const visibleReviews = useMemo(
    () => reviews.filter((r) => {
      if (r.employeeSubmittedAt) return true;
      const status = inferReviewWorkflowStatus(r);
      return status !== ReviewWorkflowStatus.DRAFT && status !== ReviewWorkflowStatus.IN_PROGRESS;
    }),
    [reviews]
  );

  const filteredReviews = useMemo(
    () => filterReviewsBySearch(visibleReviews, reviewSearchText, allCycles),
    [visibleReviews, reviewSearchText, allCycles]
  );

  const selfReviewBanner = activeCycle && (
    <div className="pms-self-review-banner-wrap">
      <div className={`pms-self-review-banner pms-self-review-banner--${selfReviewProgress.toLowerCase()}`}>
        {selfReviewProgress === SelfReviewProgress.NOT_STARTED && canStartSelfReview && (
          <>
            <span>Self review is open for <strong>{activeCycle.name}</strong>.</span>
            <button type="button" className="pms-btn pms-btn-primary" onClick={() => setSelfReviewPanelOpen(true)}>
              Start Self Review
            </button>
          </>
        )}
        {selfReviewProgress === SelfReviewProgress.NOT_STARTED && !canStartSelfReview && activeCycle.reviewPhase === ReviewPhase.CLOSED && (
          <span>🔒 Self review for <strong>{activeCycle.name}</strong> is closed. For changes please contact HR.</span>
        )}
        {/*
          The not-yet-opened case. The three branches around this one cover open, closed and
          submitted, which left the phase HR has not opened yet — NOT_STARTED, or absent on a
          cycle created before reviewPhase was written — rendering an empty banner box: no
          button, no explanation, nothing to tell the employee whether they were early or
          something was broken. Saying so is the point of the banner.
        */}
        {selfReviewProgress === SelfReviewProgress.NOT_STARTED && !canStartSelfReview
          && activeCycle.reviewPhase !== ReviewPhase.CLOSED && (
          <span>
            ⏳ Self review for <strong>{activeCycle.name}</strong> has not opened yet.
            You will be able to submit it once HR opens the review.
          </span>
        )}
        {selfReviewProgress === SelfReviewProgress.SELF_SUBMITTED && (
          <span>✅ You submitted your self review for <strong>{activeCycle.name}</strong>
            {myReviewForActiveCycle?.employeeSubmittedAt
              ? ` (${formatReviewDate(myReviewForActiveCycle.employeeSubmittedAt)} · ${formatRelativeTime(myReviewForActiveCycle.employeeSubmittedAt)})`
              : ''}.
            Waiting for your manager's review.
          </span>
        )}
        {/*
          Returned for revision. Its own branch because RETURNED is a distinct progress value —
          without it the banner would render empty, and the SELF_SUBMITTED branch would
          otherwise tell the employee to wait for a manager who is waiting for them.
        */}
        {selfReviewProgress === SelfReviewProgress.RETURNED && (
          <>
            <span>
              ↩ Your manager returned this self review for revision.
              {getReturnReason(myReviewForActiveCycle) && (
                <>
                  <br />
                  <strong>Manager's reason:</strong> {getReturnReason(myReviewForActiveCycle)}
                </>
              )}
            </span>
            <button type="button" className="pms-btn pms-btn-primary" onClick={() => setSelfReviewPanelOpen(true)}>
              Edit Self Review
            </button>
          </>
        )}
        {selfReviewProgress === SelfReviewProgress.MANAGER_SUBMITTED && (
          <span>✅ Manager review completed for <strong>{activeCycle.name}</strong>.</span>
        )}
      </div>
      {activeCycle.reviewPhase && (
        <ReviewTimeline
          cycle={activeCycle}
          review={myReviewForActiveCycle}
          objectives={selfReviewObjectives}
        />
      )}
    </div>
  );

  const selfReviewPanel = (
    <RightSidePanel
      isOpen={selfReviewPanelOpen}
      title="Self Review"
      onClose={() => setSelfReviewPanelOpen(false)}
      wide
    >
      <SelfReviewForm
        objectives={selfReviewObjectives}
        previousSubmission={myReviewForActiveCycle}
        isResubmission={selfReviewProgress === SelfReviewProgress.RETURNED}
        onSubmit={handleSubmitSelfReview}
        onCancel={() => setSelfReviewPanelOpen(false)}
        submitting={submittingSelfReview}
      />
    </RightSidePanel>
  );

  if (loading) {
    return <ReviewListSkeleton />;
  }

  if (visibleReviews.length === 0) {
    /*
     * "No reviews are assigned to you" is the wrong sentence while a self-review is open.
     *
     * A Review row is created lazily — submitSelfReview looks for an existing record and
     * inserts one only on first submit — so between HR opening the phase and the employee
     * submitting, there genuinely is no review record. The list is legitimately empty, but the
     * old copy read as "there is nothing here for you", directly contradicting the banner above
     * it offering Start Self Review. Both statements were true of different things and the page
     * looked broken.
     *
     * Driven by canStartSelfReview (phase OPEN, nothing submitted yet), i.e. the same backend
     * state that decides whether the button is offered — not by suppressing the empty state.
     */
    return (
      <>
        {selfReviewBanner}
        {selfReviewPanel}
        {canStartSelfReview ? (
          <EmptyState
            title="Self review not started"
            message="Your review record is created when you submit. Use Start Self Review above to begin."
          />
        ) : (
          /*
             Says what the employee should do next rather than only that a list is empty. Which
             of the two messages applies is decided by the cycle's phase, not by the absence of
             a record: before HR opens the review there is legitimately nothing to show, and
             "no reviews are assigned to you" read as though something had gone wrong.
          */
          <EmptyState
            title="No Review Available"
            message={
              activeCycle
                ? 'Your self-review has not been opened yet. You will be able to complete it once HR opens the review.'
                : 'No review cycle is selected for this period.'
            }
          />
        )}
      </>
    );
  }

  return (
    /*
     * The banner sits beside the workspace, not inside it. It used to be the grid's first
     * child (spanning both columns via grid-column: 1 / -1), which put a bordered status
     * block inside the bordered review sheet — two edges around one thing. As siblings each
     * owns its own surface, and the empty-state path above already renders them this way.
     */
    <>
      {selfReviewBanner}
      {selfReviewPanel}
      <div className="pms-workspace">
        {/* LEFT: Review List Sidebar */}
        <aside className="pms-workspace__sidebar">
          <div className="pms-workspace__sidebar-header">
            <span className="pms-workspace__sidebar-title">Reviews</span>
            <span className="pms-workspace__sidebar-count">{filteredReviews.length}</span>
          </div>
          <input
            className="pms-workspace__sidebar-search"
            type="search"
            value={reviewSearchText}
            onChange={(e) => setReviewSearchText(e.target.value)}
            placeholder="Filter reviews..."
            aria-label="Filter reviews"
          />
          <nav className="pms-workspace__sidebar-list" role="listbox" aria-label="Review list">
            {filteredReviews.map((review) => {
              const isActive = selectedReview?.id === review.id;
              return (
                <button
                  key={review.id}
                  className={`pms-workspace__sidebar-item ${isActive ? 'pms-workspace__sidebar-item--active' : ''}`}
                  onClick={() => handleSelectReview(review)}
                  role="option"
                  aria-selected={isActive}
                >
                  <div className="pms-workspace__sidebar-item-top">
                    <span className="pms-workspace__sidebar-item-name">{getReviewTitle(review, allCycles)}</span>
                    <span className={`pms-workspace__sidebar-item-status pms-workspace__sidebar-item-status--${getReviewStatus(review, allCycles).toLowerCase().replace(/\s+/g, '-')}`}>
                      {getReviewStatus(review, allCycles)}
                    </span>
                  </div>
                  <div className="pms-workspace__sidebar-item-meta">
                    <span>{getReviewCycleName(review, allCycles)}</span>
                    {/* No rating chip on a High Level review — it carries no rating, so the chip
                        was a permanent "Not rated" on a row that was never going to have one. */}
                    {getCycleForReview(review, allCycles)?.reviewCycleType !== ReviewCycleType.HIGH_LEVEL && (
                      <RatingDisplay rating={review.overallRating || ''} compact />
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>
  
        {/* RIGHT: Review Workspace */}
        <main className="pms-workspace__main">
          {!selectedReview ? (
            <div className="pms-workspace__empty">
              <span className="pms-workspace__empty-icon">â—‡</span>
              <strong>Select a review</strong>
              <span>Choose a review from the sidebar to view details and feedback.</span>
            </div>
          ) : (
            <div className="pms-workspace__content">
              <div className="pms-review-flow__workspace pms-employee-review-workspace">
                <div className="pms-review-flow__workspace-header">
                  <div>
                    <h3>{getReviewTitle(selectedReview, allCycles)}</h3>
                    <span>{getReviewCycleName(selectedReview, allCycles)} • {getReviewStatus(selectedReview, allCycles)} • {selectedReview.reviewerName || 'Manager'}</span>
                  </div>
                  <div className="pms-workspace__actions-btns">
                    {canAcknowledgeReview(selectedReview) && (
                      <button className="pms-workspace__btn-primary" onClick={handleAcknowledgeReview} disabled={acknowledging}>
                        {acknowledging ? 'Acknowledging...' : 'Acknowledge Review'}
                      </button>
                    )}
                    <button className="pms-workspace__btn-secondary" onClick={handleExportReview}>
                      Export PDF
                    </button>
                    {/*
                      Closes the opened review and returns to the list.
                      Opening a review had no matching way to close it — the detail stayed
                      expanded under the list until another review was clicked, and with one
                      review in the cycle there was nothing else to click. ManagerView already
                      has this affordance (pms-review-flow__clear-btn on the selected employee);
                      this is the same idea on the employee's side.
                    */}
                    <button
                      type="button"
                      className="pms-review-flow__clear-btn"
                      onClick={() => setSelectedReview(null)}
                      title="Close review"
                      aria-label={`Close ${getReviewTitle(selectedReview, allCycles)}`}
                    >
                      ✕
                    </button>
                  </div>
                </div>
  
                <div className="pms-review-flow__tabs">
                  <button className={`pms-review-flow__tab ${activeEmployeeTab === 'objectives' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveEmployeeTab('objectives')}>Objectives {selectedReview.objectiveRatings.length > 0 && <span className="pms-review-flow__tab-badge">{selectedReview.objectiveRatings.length}</span>}</button>
                  <button className={`pms-review-flow__tab ${activeEmployeeTab === 'overview' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveEmployeeTab('overview')}>Overview</button>
                  <button className={`pms-review-flow__tab ${activeEmployeeTab === 'feedback' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveEmployeeTab('feedback')}>Feedback</button>
                  <button className={`pms-review-flow__tab ${activeEmployeeTab === 'discussion' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveEmployeeTab('discussion')}>
                    Discussion
                    {getReviewDiscussion(selectedReview).length > 0 && <span className="pms-review-flow__tab-badge">{getReviewDiscussion(selectedReview).length}</span>}
                  </button>
                  <button className={`pms-review-flow__tab ${activeEmployeeTab === 'timeline' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveEmployeeTab('timeline')}>Timeline</button>
                  <button className={`pms-review-flow__tab ${activeEmployeeTab === 'attachments' ? 'pms-review-flow__tab--active' : ''}`} onClick={() => setActiveEmployeeTab('attachments')}>Attachments {attachments.length > 0 && <span className="pms-review-flow__tab-badge">{attachments.length}</span>}</button>
                </div>
  
                <div className="pms-review-flow__tab-content">
                  {activeEmployeeTab === 'objectives' && (
                    <ReviewSnapshotObjectives review={selectedReview} cycle={selectedCycle} />
                  )}
  
                  {activeEmployeeTab === 'overview' && (
                    <div className="pms-employee-review-overview">
                      <div className="pms-employee-review-overview__item">
                        <span>Status</span>
                        <strong className={`pms-workspace__status-badge pms-workspace__status-badge--${getReviewStatus(selectedReview, allCycles).toLowerCase().replace(/\s+/g, '-')}`}>
                          {getReviewStatus(selectedReview, allCycles)}
                        </strong>
                      </div>
                      <div className="pms-employee-review-overview__item">
                        <span>Manager</span>
                        <strong>{selectedReview.reviewerName || 'Not assigned'}</strong>
                      </div>
                      <div className="pms-employee-review-overview__item">
                        <span>Review Cycle</span>
                        <strong>{getReviewCycleName(selectedReview, allCycles)}</strong>
                      </div>
                      {getReviewTypeLabel(selectedReview, allCycles) && (
                        <div className="pms-employee-review-overview__item">
                          <span>Review Type</span>
                          <strong>{getReviewTypeLabel(selectedReview, allCycles)}</strong>
                        </div>
                      )}
                      <div className="pms-employee-review-overview__item">
                        <span>Submission Date</span>
                        <strong>{formatReviewDate(selectedReview.submittedAt || selectedReview.publishedAt || selectedReview.updatedAt || selectedReview.createdAt)}</strong>
                      </div>
                      {/*
                        Hidden for a High Level review, for the same reason as the Rating column
                        in the objectives table: handleSubmitFinalReview drops the rating
                        requirement for high-level cycles and the manager is given no control to
                        set one, so this row could only ever read "Not Rated" — reporting the
                        absence of a value the review type never collects.
                      */}
                      {selectedCycle?.reviewCycleType !== ReviewCycleType.HIGH_LEVEL && (
                        <div className="pms-employee-review-overview__item">
                          <span>Rating</span>
                          <strong>{selectedReview.overallRating ? getRatingLabel(selectedReview.overallRating as RatingScale) : 'Not Rated'}</strong>
                        </div>
                      )}
                      <div className="pms-employee-review-overview__item">
                        <span>Acknowledgement</span>
                        {selectedReview.isAcknowledged
                          ? <strong className="pms-workspace__ack-done">Acknowledged {selectedReview.acknowledgedAt ? `on ${formatReviewDate(selectedReview.acknowledgedAt)}` : ''}</strong>
                          : <strong className="pms-workspace__ack-pending">Pending Acknowledgement</strong>
                        }
                      </div>
                    </div>
                  )}
  
                  {activeEmployeeTab === 'feedback' && (
                    <div className="pms-review-flow__feedback">
                      {/* The employee's own submission, read back. Above the manager's fields
                          because it came first, and clearly labelled as theirs so the two are
                          never mistaken for one another. */}
                      <EmployeeSelfReviewSummary review={selectedReview} audience="employee" />
                      <div className="pms-review-flow__field">
                        <label>Manager Feedback</label>
                        <div className="pms-workspace__feedback-content">
                          {selectedReview.overallComments || <em className="pms-workspace__no-content">No feedback provided yet.</em>}
                        </div>
                      </div>
                      <div className="pms-review-flow__field">
                        <label>Development Suggestions</label>
                        <div className="pms-workspace__feedback-content">
                          {selectedReview.developmentSuggestions || <em className="pms-workspace__no-content">No development suggestions yet.</em>}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeEmployeeTab === 'discussion' && (
                    <ReviewDiscussion
                      messages={getReviewDiscussion(selectedReview)}
                      viewerRole="employee"
                      onSend={handlePostDiscussionMessage}
                      sending={postingMessage}
                      error={discussionError}
                      disabledReason={
                        getCycleForReview(selectedReview, allCycles)?.isLocked
                          ? 'This review cycle is locked by HR, so the discussion is read-only.'
                          : undefined
                      }
                    />
                  )}
  
                  {activeEmployeeTab === 'timeline' && (
                    <>
                      {/* Workflow position, then the audit trail — see the manager tab above. */}
                      <ReviewTimeline
                        cycle={selectedCycle}
                        review={selectedReview}
                        objectives={selfReviewObjectives}
                      />
                      <ReviewHistoryTimeline review={selectedReview} cycle={selectedCycle} />
                    </>
                  )}
  
                  {activeEmployeeTab === 'attachments' && (
                    <div className="pms-workspace__attachments">
                      {attachmentsLoading ? (
                        <p className="pms-workspace__no-content">Loading attachments...</p>
                      ) : attachments.length === 0 ? (
                        <p className="pms-workspace__no-content">No attachments yet.</p>
                      ) : (
                        <ReviewAttachmentList attachments={attachments} />
                      )}
                      {attachmentError && (
                        <p className="pms-workspace__attachment-error" role="alert">{attachmentError}</p>
                      )}
                      <button
                        className="pms-workspace__btn-secondary"
                        onClick={handleAddAttachment}
                        disabled={attachmentUploading || selectedReview.isAcknowledged || !!getCycleForReview(selectedReview, allCycles)?.isLocked || inferReviewWorkflowStatus(selectedReview) === ReviewWorkflowStatus.SUBMITTED}
                      >
                        {attachmentUploading ? 'Uploading…' : '+ Add Attachment'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default TeamReviews;
